import { TIER_MAPS, getTierForRank, QB_SFLEX_TAIL } from '../data/tiers';
import { MOCK_ROSTER_SIZE } from '../data/archetypes';
import { getAdpPosRank, parseRankNum, getEffectivePosRank } from '../data/adpData';
import { UNIQUE_PLAYERS } from '../data/players';

// ============================================================
// MOCK DRAFT AI ENGINE (pure functions)
// ============================================================

// Diagnostic collector — set _bidDiag.data = [] to enable, null to disable
export const _bidDiag = { data: null };

// ── Soft cap: board-anchored excess damping ─────────────────────────
// `mode` selects the ceiling form; `legacy` is the pre-Aug-6 multiplicative
// damp, kept ONLY so a probe can A/B in one build. Diagnostics write these,
// as does utils/devDials.js (dev-only, stripped from production builds); no
// shipped src/hooks or src/components path does.
//
//   disabled  no ceiling at all (the $74 absolute cap still applies)
//   legacy    maxBid *= max(0.50, 1 - (maxBid/budget - t) * m)
//   (default) board-anchored excess damping, below
//
// WHY THE FORM CHANGED. The legacy damp multiplies the WHOLE bid by a factor
// that declines in the bid, i.e. y = x*(a - bx) — a downward parabola. It has
// an interior maximum ($64 at pre-damp $80, 10-team $200) and falls to $55 by
// pre-damp $110, so 30.2% of top-of-board winning bids sat in a region where
// valuing a player more produced a lower bid. It is also keyed on
// maxBid/startingBudget, which is board-blind: every player whose bid clears
// the threshold lands in the same dollar window regardless of whether the
// board says $62 or $46. That is the top-of-board compression.
//
// The replacement damps only the EXCESS above a board-anchored allowance:
//
//   anchor = max(board * (1 + headroom), floorFrac * startingBudget)
//   f(x)   = x                            x <= anchor
//          = anchor + excessPass*(x-anchor)  x >  anchor
//
// MONOTONE BY CONSTRUCTION, not by fit: f is continuous at x = anchor and
// piecewise-linear with slopes 1 and excessPass, both > 0, so f'(x) > 0 for
// every x >= 0 and every anchor. Composing with the $74 absolute cap gives
// min(f(x), 74), non-decreasing as the pointwise min of two non-decreasing
// functions. No input can produce "more valuation, less bid".
//
// BOARD-AWARE: anchor scales with the player's displayed price, so the ceiling
// is a different number for a $62 board than a $46 board. floorFrac keeps the
// deep board out of it — a $1-board sleeper is not squeezed to $1.
//
// posMult IS GONE FROM HERE, deliberately (resolves e1e737d). That commit
// multiplied the legacy threshold by posMult so a calibrated league paying 38%
// of budget for its top RB got a 38% threshold, but posMult is 1 unless the
// price map carries __leagueMultipliers — so in every uncalibrated league,
// batch sim and probe it was inert, and the seam it names ("a $72 calibrated
// intent dampened back to ~$62") stayed open in the default config. Anchoring
// on the board price is the general form of what posMult approximated:
// usePricing already folds leagueMultipliers into dynamicPrices, so the anchor
// carries the calibration in calibrated leagues AND tracks the board in
// uncalibrated ones. One mechanism, fires everywhere.
// FITTED VALUES (Aug 6, two-stage fit — see the commit message).
// floorFrac was set FIRST, against the ranks 4-8 control: it decides where the
// ceiling engages, and 0.30 is the smallest value at which the control's
// top-two cap gap distribution is statistically unchanged from the legacy damp
// (Welch on per-draft means p=0.31, two-sample KS p=0.49; every F <= 0.25
// changed it, F=0.10 collapsing it from $8.27 to $2.89). headroom and
// excessPass were then fitted at that F against ranks 1-3 only.
//
// excessPass = 0.30 is the gap knob, and it is a gap knob by identity: for two
// bidders above the anchor, cap1 - cap2 = excessPass * (pre1 - pre2), so the
// surviving spread between the top two caps is exactly excessPass times the
// pre-damp spread. The legacy damp sat near the peak of its own parabola where
// that derivative is ~0, which is why the top two caps converged to within $1-2
// and two +$1 raises ended the auction.
//
// The same identity means excessPass ALSO sets how far above the anchor sales
// land, so it cannot serve the sale/board target at the same time. That target
// is deliberately not pursued here: ranks 4-8 belong to the band-mean work, and
// the residual at ranks 1-3 is near-uniform (dispersion across the six cells
// 0.099 -> 0.087 as the mean rises), so ordering is preserved and the level is
// correctable from the board side. An affine shift, f(x) = x - c(board), would
// decouple level from spread and is the natural follow-up; `legacy` and the
// scratch harness are kept so it can be A/B'd the same way.
export const _softCap = {
  disabled: false,
  legacy: false,
  headroom: 0.00,      // allowance above board before damping starts
  excessPass: 0.30,    // share of the excess that survives — the gap knob
  floorFrac: 0.30,     // anchor floor as a fraction of startingBudget
  absCapFrac: 0.37,    // maxSinglePlayer as a fraction of startingBudget.
                       // PROBE ONLY — 0.37 is the shipped value; no shipped
                       // src/hooks or src/components path writes it (see the
                       // devDials note above).
  // D3 (chunk 5 part 2) — SHIPPED as an engine default in chunk 9. Board term
  // in the anchor's floor leg. The pre-D3 floor leg was flat: F*B = $60 at every
  // board price, so the anchor was max(board*(1+h), 60) and could not tell a $62
  // board from a $33 one below the crossover. boardTerm makes that leg affine in
  // the board:
  //
  //     floor leg = (F*B - boardPin*boardTerm) + boardTerm*board
  //
  // pinned so that a board of `boardPin` keeps exactly the old flat floor.
  //
  // boardTerm = 0 collapses to the flat leg and takes the pre-D3 expression
  // verbatim, so the old identity digest stays exactly reachable from the probe.
  boardTerm: 0.375,
  // null = DERIVE from the format (see `derivePin`). A number overrides the
  // derivation outright and is how every probe stage pins a cell.
  boardPin: null,
};

// ── SFLEX punt dials (aug13 spend-down sweep) — PROBE ONLY ──────────
// Both values below ARE the shipped constants; nothing here changes behaviour
// at the defaults. The object exists so the aug13 sweep can pin the two
// existing punt levers per cell without editing src/ between cells — the same
// seam `_softCap` and `_demandDials` provide. No shipped src/hooks,
// src/components or src/engine path writes it.
//
//   puntTierMult — the Punt QB SF tier-1-3 multiplier at :1150. 0.15 shipped.
//   puntSeatWeight — P(Punt QB SF) in the SFLEX strategy roll
//     (simulation.js:310, which reads this object). 1/3 shipped; the roll's
//     Elite share is held at 1/3 and Balanced absorbs the remainder, so
//     halving punt rebalances toward Balanced exactly as the brief specifies.
//
// AUG 14 UPSTREAM SWEEP — the aug13 sweep measured both dials above as near-
// inert (a 3.3x change on puntTierMult flipped ~zero picks: punt bids already
// clear below the cap). The five dials below are the UPSTREAM suppressors that
// actually bind, per the chunk-1 site table in
// docs/planning/sflex-spend-down-aug13.md. Every one is its shipped constant.
//
//   puntWillingness   — Punt QB SF willingness override, :622.        0.85
//   puntDampener      — Punt QB SF scarcity-curve dampener, :422 in
//                       calcAiMaxBidV2 and :1300 in calcAiOpeningBid.  0.40
//   puntQb$           — MODIFIERS["Punt QB SF"].overrides.qb$
//                       (archetypes.js:149), re-applied at
//                       simulation.js:325 so the sweep can pin it.      30
//   puntQbFloor       — Punt QB SF posBudget floor fraction, :674.     0.12
//   lateSpikeT3       — the tier<=3 late-spike ceiling inside the
//                       must-fill net, :1074. NOT punt-specific: it
//                       caps the pick>80 tier-spike for EVERY seat and
//                       EVERY position. Swept here because the site
//                       table names it as a binding suppressor of late
//                       QB money; read cell deltas with that breadth
//                       in mind.                                        18
//   balancedDampener  — Balanced QB SF scarcity dampener at the same
//                       two sites. Not a punt lever at all — it exists
//                       for the aug14 "NONE+" probe cell, which asks
//                       one question past the punt question about
//                       Balanced depth pricing.                        0.70
//   maxQbFix          — the aug14 maxQB roster-cap fix (simulation.js
//                       buildDraftTeams). SHIPPED ON. Superflex QB targets are
//                       derived from the roster (floored at QB + SFLEX)
//                       instead of the 1QB 50/40/10 table, and the
//                       `profile.qb$ >= 35` escape no longer decides roster
//                       legality. Gated on sflexCount > 0, so 1QB is
//                       byte-untouched at either dial state; setting it false
//                       must reproduce the pre-fix digest exactly.          true
//   maxQbTopOffset    — the TOP of the superflex QB-target range, as an offset
//                       above QB + SFLEX for the last 10% of seats. 1 SHIPPED
//                       (targets 2/3 in standard superflex). 2 reproduces the
//                       aug14 first pass (2/3/4), which regressed the deep room
//                       by opening a 4-QB hoarding channel; the control cell of
//                       the top-fix measurement runs at 2.                     1
//
// (`qb2Rescue` lived here from b00b033 until its revert on aug14. The rescue was
// measured dead weight once the maxQB roster cap stopped filtering its targets
// out of the auction: +0.04pp of QB share, 0% vs 0% one-QB seats. The defect
// class it addressed stays recorded in docs/data/sflex-qb2-mustfill-aug14.md.)
export const _sflexDials = {
  puntTierMult: 0.15,
  puntSeatWeight: 1 / 3,
  puntWillingness: 0.85,
  puntDampener: 0.40,
  puntQb$: 30,
  puntQbFloor: 0.12,
  lateSpikeT3: 18,
  balancedDampener: 0.70,
  maxQbFix: true,
  maxQbTopOffset: 1,
  // ── The 2QB gate — SHIPPED aug24 (QB>=2 rosters only) ─────────────
  // `maxQbFix` above repairs the QB roster-cap ladder, but simulation.js gated
  // it on `sflexCount > 0`, so a TRUE-2QB league (QB:2, SFLEX:0) fell to the
  // legacy branch and a seat with qb$ < 35 kept a maxQB of 1 in a format that
  // mandates two QB starters. `maxQbGatePremium` swaps that gate to the shared
  // `isQbPremiumFormat` predicate — the same swap the pricing fix made, no new
  // constant and no new threshold; `qbRequired` already computes 2 for such a
  // roster.
  //
  // WHY IT MATTERS MORE THAN A ROSTER COUNT. `maxQB` feeds the bidder-selection
  // gate (`pos === "QB" && pCount >= maxQB`), which drops a seat from every QB
  // auction the moment it owns one quarterback — UPSTREAM of calcAiMaxBidV2, so
  // no bid-side seam can reach it. Measured at n=200 on a 12tm/$200 corpus-modal
  // 2QB config (docs/data/qb2qb-ai-aug24.md): 12.33% of seats carried maxQB 1
  // and 8.88% finished the draft with one QB — an illegal lineup — against 0%
  // and 0% here. Prices moved only as far as the freed bidders moved them
  // (QB13-24 $7.58 -> $7.40, league unspent +$0.42).
  //
  // The FALSE branch is retained, not dead: it is the aug14 expression verbatim
  // and it is what the probe's OFF arm runs, so the before-picture stays
  // reproducible from HEAD instead of needing a checkout.
  //
  // 1QB and superflex are byte-identical across this flip BY CONSTRUCTION — the
  // predicate's first clause IS `sflexCount > 0`, so the two gates disagree only
  // on QB>=2. Asserted, not assumed: Smithtown 3849743216, sflex att 2322851037,
  // sc 1293885058, all unmoved at both settings. (Those three are the AUG24
  // values and are still what THIS flip's two arms return; they are no longer
  // what HEAD returns — the aug26 flat-curve ship moved all of them. See
  // `_flatCurve` below and FLATCURVE_SHIPPED in softcapProbe.mjs.)
  maxQbGatePremium: true,
};

// ── The Hammer — the rank arm, SHIPPED for superflex (aug19) ─────────
//
// SHIPPED, not a measurement seam. `trueHammer: true` at the measured defaults
// accumDampen 0.80 / swingMult 1.20: in a superflex room the rank comparison IS
// the deploy condition as of this commit. (The prior text here read MEASUREMENT
// ONLY — that is no longer true.)
//
// WHAT WAS WRONG. The old deploy condition counts rivals whose budget exceeds a
// hardcoded $80. The hammer is not a COUNT question, it is a RANK one — "is my
// top affordable bid the highest in the room?" — so in a 12-team/$240 room the
// census waited on a threshold that never cleared and the seat banked to the end
// of the draft. The rank branch replaces the whole census, reusing the
// `budget - (emptySlots - 1)` capacity `hardCap` (:485), getOpponentMaxBid
// (:1551) and the endgame scan (:1192) already compute — no new plumbing.
//
// WHAT IS RETAINED. The census else-branch is NOT deleted. It remains (a) the
// `trueHammer: false` fallback and (b) the live 1QB path: the rank branch is
// gated on `sflexCount > 0`, so standard-format rooms take the census VERBATIM
// and 1QB is byte-identical across the flip (Smithtown gate 3849743216, before
// and after). aug15 measured the 1QB Hammer as benign ($1.55-2.12 seat unspent
// across all three arms), so there was nothing to buy there and a real control
// to keep.
//
// WHAT THE CONSTANTS REST ON. aug15 three-arm ablation -> aug16 sflex scoping
// -> aug17 deletion gap (keep, pill isolated) -> aug17 + aug18 constant sweeps
// -> aug18 re-score under ABSOLUTE wins-while-holding -> aug19 fair-bar
// re-check with C1 re-anchored to the SHIPPED arm rather than to a tuned cell.
// 0.80/1.20 survived every re-ranking including the one built to break it:
// docs/data/hammer-rescore-aug18.md and its companion
// docs/data/hammer-rescore-fairbar-aug19.md. Measured gain, sflex Hammer-seat
// stranding: $21.08 -> $12.86 (attribution) and $18.24 -> $12.00 (scorecard).
// The shipped digests ARE the measured arm-C digests, bit for bit: sflex
// attribution 3606689934, sflex scorecard 584396122.
//
//   accumDampen — the single ACCUMULATE dampen replacing the 0.55/0.80 bands.
//   swingMult   — the SWING willingness boost, applied only on players in the
//                 seat's own unfilled shopping-list tiers and bounded by the
//                 tier ceiling. Measured winners — re-tuning them means
//                 re-running the sweeps, not editing here.
//   nominateWhenHolding — OFF, and not a ship candidate. At HEAD nomination
//                 never consults hammer state: through pick 50 the seat
//                 poison-pills the elites it is trying NOT to buy, and after
//                 that it falls to the generic scarce-tier/self-buy paths, so
//                 the seat can only swing when the ROOM happens to nominate one
//                 of its targets. With this on, a seat that HOLDS the hammer
//                 puts its own target on the block instead. aug19 graded it
//                 NO-SHIP but WEAKLY: the branch fired on only 14-21% of
//                 opportunities because the hold test sits BELOW the
//                 my-guy/sleeper/tax tiers in the nomination flow, so the grid
//                 largely measured a lever that never pulled
//                 (docs/data/hammer-nom-aug19.md). The hypothesis is not
//                 closed; it is queued for a proper top-of-flow test.
export const _hammerArm = {
  trueHammer: true,
  accumDampen: 0.80,
  swingMult: 1.20,
  nominateWhenHolding: false,
};

// ── THE QB TAIL — seam A SHIPPED aug22, seam B pending ──────────────
//
// LINEAGE. The defect: docs/data/qbtail-aug20.md measured superflex QB prices
// against the real corpus and found QB1-12 fine (1.05x real) while QB13-24
// cleared at 0.62x and the late window collapsed to 0.27x. The trace naming the
// causes: docs/data/qbtail-chunk1-aug20.md. The 2x2 ablation that decided which
// cause to fix: docs/data/qbfix-aug21.md (n=200 x 9 disjoint replicate blocks,
// both sflex configs, four arms over these two seams).
//
// BOTH seams are scoped to `sflexCount > 0` — the same conjunct the rank hammer
// uses — so 1QB is byte-identical at EITHER dial state by construction, not
// merely at a default. The Smithtown gate reads 3849743216 across the flip.
// (3849743216 is the PRE-AUG26 gate. The flat-curve ship has no format gate and
// moved it to 5952498; this seam's byte-identity claim is unaffected — it is a
// statement about this dial, measured at its own arms.)
//
//   sflexTierBranch — SHIPPED aug22. P2, the BOARD level. `TIER_MAPS.QB` has no
//                     superflex branch: tier 6 is one flat $2-3 band running
//                     from QB19 to QB999, and it drives the AI's bid anchor. In
//                     superflex, QB13+ now draws its floor/ceiling from
//                     QB_SFLEX_TAIL (tiers.js) at two sites and only two:
//                       · the tierValue anchor in calcAiMaxBidV2 (`qbBand`), and
//                       · the opening-bid tier midpoint in calcAiOpeningBid
//                         (`obQbBand`) — the nominator IS the opening bidder, so
//                         a tail QB nobody outbids sells at exactly that number
//                         and leaving the opening on the 1QB ladder would cap
//                         the seam's reach at the bids nobody makes.
//                     QB1-12 is untouched at either state, and tier NUMBERS
//                     never move — countSubstitutes, the tierAnchors key, the
//                     shopping lists and every `playerTier <=` test read exactly
//                     what they read before the flip.                        ON
//                     WHAT IT BOUGHT (qbfix-aug21): QB13-24 mean $ +1.27 and
//                     +1.05 against control replicate spreads of 0.42 and 0.46
//                     — the only effect in the 2x2 that cleared its own noise
//                     bar, and it cleared on BOTH configs. QB1-12 held
//                     (35.79->35.62, 42.61->42.63), QB25-32 held, no new
//                     scorecard ❌, and the money came out of unspent (league
//                     unspent -2.8 / -2.3, stranded seats down).
//                     🔴 The BAND LEVELS are PROVISIONAL — first-pass values off
//                     a 10-draft, ~4-family corpus. Shipping the seam is not a
//                     claim that its constants are tuned; that is a sweep, and
//                     it is a separate session.
//   benchQbBid      — OFF. MEASURED AT POWER AUG 22 AND IT DID NOT SEPARATE;
//                     this is a settled answer, not an open question. P1/P3, the
//                     BIDDING side. A seat at its `maxQB` is dropped from the
//                     bidder Set at simulation.js:858 BEFORE calcAiMaxBidV2
//                     runs, and six of twelve sflex seats carry maxQB 2, so half
//                     the room leaves the QB market for good once it fills QB +
//                     SFLEX. On, and in superflex, such a seat stays in the pool
//                     for STARTER-CLASS QBs only, at a capped bid. Implemented
//                     at the gate itself — see the block at simulation.js:858.
//                     THE VERDICT (docs/data/qbfix-confirmb-aug22.md, n=600 x 9
//                     blocks, baseline = shipped HEAD): P1 on the graded config
//                     moved Δ-0.85 against a baseline replicate spread of 1.15.
//                     NOTHING separated — no prior on either config, and no cost
//                     row either. Guards were clean (no new ❌, Hammer seat not
//                     up), so it failed on evidence, not on a side effect.
//                     🔴 DO NOT re-run this at a bigger n on the aug21 power
//                     math. That sizing assumed the effect was real and fixed:
//                     the bar shrank as predicted (1.59 -> 1.15) but the DELTA
//                     shrank WITH it (-1.04 -> -0.85), which is what a mostly-
//                     noise delta looks like. The ratio barely moved (0.65 ->
//                     0.74) and extrapolating to n~1100 assumes the stability
//                     this run refutes. Direction HAS been consistent across
//                     both reads and every prior — that is a hypothesis worth a
//                     STRONGER seam, not a bigger sample.
//                     🔴 The cap (0.35 x sticker, $6-on-$200) was never swept, so
//                     this is a verdict at these constants, not on the
//                     mechanism.                                             OFF
//
// 🔴 NAMED OPEN DEFECT, not addressed here: the LATE window. QB13-24 sold past
// the late pick threshold still clears at 0.2-0.4x real at EVERY arm of the 2x2,
// so neither seam is its cause. Candidates are the starter lockout, budgetMod
// collapse, and the endgame scan. It needs its own measurement brief.
export const _qbTail = { sflexTierBranch: true, benchQbBid: false };

// ── THE FLAT CURVE — the early-nomination damper ──  RETRACTED aug27, OFF
//
// 🔴 SHIPPED aug26 at earlyDamp 0.60, ALL FORMATS — AND RETRACTED aug27. The dial
// is back at 1.0 (OFF) and production is back on the pre-aug26 stream: Smithtown
// gate 3849743216, sflex att 2322851037 / sc 1293885058. The ship record below is
// KEPT IN FULL, not deleted — the evidence that motivated the damper is still
// good; only its SHAPE was wrong.
//
// WHY IT WAS RETRACTED — the shape ate the front of the window.
// Owner smoke on a 10tm/$200 1QB room, same player class, nomination slot the
// only variable:
//     Gibbs  $49  at pick 1
//     Bijan  $68  at pick 11
//     Chase  $70  at pick 24
// A ~$19 spread produced by WHEN a player was nominated, which is precisely the
// defect the damper was built to remove — re-created with the sign flipped. The
// mechanism is the ramp: 0.60 AT pick 1 ramping linearly to 1.0 across two rounds
// bites hardest exactly where the fewest picks are, so picks 1-4 are crushed while
// 15-20 drift hot.
//
// 🔴 WHY THE SWEEP DID NOT CATCH IT, and this is the durable lesson: the sweep's
// gate was BUCKET AGGREGATES (picks 1-10 geomean -> 1.059 ✓). A bucket can average
// flat while being tilted inside it — an over-correction at 1-4 nets against an
// under-correction at 8-10 and the mean looks like success. Real rooms are flat at
// EVERY pick, not on average over ten of them. AGGREGATE-FLAT IS NOT PROFILE-FLAT.
// Any successor shape must be gated on a per-pick (micro-bucket) profile, with
// picks 1-3 an explicit no-exception cell.
//
// RESHAPE PENDING. Candidate shapes are a CONSTANT multiplier across the early
// window (no ramp) and a TWO-STEP (one level over picks 1-10, a milder one over
// 11-20, i.e. the inverse of the measured 1.22x / 1.11x premium applied directly).
// Note for whoever runs it: price response to this damper is SUBLINEAR — the aug26
// sweep's 0.80 linear-ramp arm landed 1.175, not 1.02 — so a constant grid may
// need lower values than the arithmetic suggests. Let the cells say.
//
// THE ORIGINAL SHIP RECORD FOLLOWS, unedited. It remains the reason a damper
// belongs here at all; see FLATCURVE_SHIPPED in softcapProbe.mjs for the digest
// lineage, now carrying its own retraction event.
//
// THE LINEAGE, in the order the evidence was taken:
//   1  ATTRIBUTION  docs/data/flatcurve-attribution-aug25.md — the tilt is real
//      and `elite-gone-deflation`'s missing first step carries 64-71% of it on
//      all three configs. `core:budgetMod` is ~1/6; `live-inflation` does not
//      register. The damper's shape follows from that attribution, not taste.
//   2  SWEEP        docs/data/flatcurve-sweep-aug25.md — 0.60 is the MILDEST
//      cell that reaches flat on both superflex configs.
//   3  CHECKS       docs/data/flatcurve-checks-aug25.md — the real-unspent cost
//      is priced, and the Smithtown Rd-1 target is shown to be compositional.
//   4  METRIC PROBE docs/data/rank-ladder-aug14.md — the 1QB objection was the
//      ESTIMATOR. On the price-rank basis the targets were actually built from,
//      the Smithtown control ran $5.00 HOT against its own four seasons ($52.78
//      vs $47.78) and 0.60 lands it at $50.02, inside the band. The nomination
//      row was the only thing failing, and it was never a real comparison.
//
// THE RECORDED COST, stated because it is real: league unspent on the $240
// config goes 1.11% -> 1.50% against a real 0.17%. Real rooms clear their money
// better than the sim does at either arm; the damper widens that gap. It is a
// mechanism-assisted ceiling, accepted knowingly, not an unmeasured side effect.
//
// WHAT THIS DAMPER DOES NOT REACH. The 41-120 pick window runs hot against real
// in BOTH formats (+0.25/+0.30/+0.41 at 41-80, +0.06/+0.50/+0.61 at 81-120) and
// this ramp ends at rounds x teams, so it cannot touch any of it. That window is
// the named successor defect and it is unowned. Do not read a flat early window
// as a flat curve.
//
// THE DEFECT. Relative price — a sale ÷ that player's own median in the same
// regime × format × season — is FLAT in real rooms at every nomination bucket
// (median exactly 1.000 across 66 harvested auctions, keeper-trimmed:
// docs/data/flatcurve-real-reference-aug25.md). In the sim it is not: picks 1-10
// clear a median of 1.18-1.22× on all three calibration configs. Sleeper's
// `pick_no` is ground-truthed as nomination order, so this is a timing effect,
// and dividing by the player's own median removes WHO was nominated and leaves
// only WHEN.
//
// WHY THE DAMPER HAS THIS SHAPE, AND NOT A PRICE HAIRCUT. The chunk-1 stage
// attribution (docs/data/flatcurve-attribution-aug25.md) ranks
// `elite-gone-deflation` first on ALL THREE configs — ~2/3 of the decomposable
// tilt — and the mechanism is an ABSENCE rather than a boost. That site is a
// draft-phase deflation ladder with exactly two steps:
//     if (eliteCount >= 8) maxBid *= 0.75; else if (eliteCount >= 6) maxBid *= 0.85;
// and both fire only AFTER the elites are gone. Nothing damps the early window,
// so the early window is simply the one window left undamped. The damper below
// is therefore the MISSING FIRST STEP of a ladder that already exists, applied
// at the same site, in the same multiplicative form — not a new mechanism and
// not a haircut on the board.
//
// WHY MULTIPLICATIVE ON MAX BIDS REACHES THE ORDER STATISTIC. The price is the
// second-highest of the field, not one seat's number, and early fields are big
// (8.5-10.4 willing bidders vs 4.1-7.5 by pick 41-80) because every seat is
// still rich. Scaling every bidder's ladder by the same factor scales the
// second-highest by that factor too, so this reaches the selection effect that
// no additive rule could.
//
// earlyDamp is the multiplier at pick 1 and ramps LINEARLY back to 1.0 across
// the first `earlyRounds` nomination rounds — settings-derived (rounds × teams),
// never a hardcoded pick count. 1.0 = OFF, and OFF is still byte-inert by
// construction: the call site tests the dial before it computes anything, which
// is what lets the pre-ship pin restore the old engine exactly.
//
// NO FORMAT GATE, deliberately. The defect was measured in 1QB and superflex
// alike and the owner's decision is all-formats. The sweep's superflex-only
// recommendation was written while the 1QB Rd-1 row appeared to block damping;
// the metric probe removed that objection (step 4 above), so the scope is the
// scope of the defect.
//
// 🔴 RETRACTED aug27 — BACK TO 1.0 (OFF). See the retraction note at the top of
// this block. The linear ramp is the thing that failed, not the decision to damp:
// `earlyDamp` is the multiplier AT pick 1, so the ramp's deepest cut lands on the
// handful of picks that are most visible and most exploitable. Production is
// deliberately back on the known early-heat artifact, which looks worse and is
// less exploitable than a pick-1 steal.
// `shape` (aug27) generalizes the seam so the reshape sweep can try non-ramp
// profiles through the SAME call site. null = the legacy linear ramp above, which
// is what `earlyDamp`/`earlyRounds` describe. OFF is now "earlyDamp 1 AND no
// shape" — see `flatCurveActive`.
//
//   { kind: 'constant', damp: 0.80, picks: 20 }
//       one flat multiplier across picks 1..picks, no ramp. The shape the
//       retraction points at: a ramp's problem is that it is not constant inside
//       the window it is graded on, and a constant trivially is.
//   { kind: 'steps', steps: [[10, 0.80], [20, 0.90]] }
//       piecewise-constant by pick ceiling, first match wins, 1.0 past the last.
//       Lets the damper mirror the measured premium's own shape (1.22x over
//       picks 1-10, 1.11x over 11-20) instead of a straight line through it.
//   { kind: 'taper', damp: 0.70, full: 16, end: 24, handoff: 6 }
//       constant `damp` through pick `full`, then a graduated return to 1.0 by
//       pick `end` — UNLESS `elite-gone-deflation` has already engaged, in which
//       case the taper ends immediately.
//
//       🔴 WHY THE HANDOFF EXISTS (aug27). The deep-constant grid was tail-safe
//       but failed per-pick flatness at 22-24, just outside its own pick-20
//       window, and failed WORSE as the constant deepened (1.322 -> 1.388 on
//       Smithtown). A hard-edged window does not remove the early premium, it
//       RELOCATES it past the edge. The fix is to not have an edge: this damper
//       was introduced as the MISSING FIRST STEP of the eliteCount ladder
//       (`if (eliteCount >= 8) 0.75; else if (eliteCount >= 6) 0.85`), so the
//       natural place for it to stop is exactly where that ladder starts.
//       `handoff` is the eliteCount at which the real ladder takes over; at or
//       above it this returns 1 and the shipped deflation does the work. `end` is
//       the backstop for rooms where the ladder engages late or never.

//
// 🔴 THESE SHAPES ARE KEYED ON ABSOLUTE PICK NUMBER, not on rounds x teams —
// DECIDED aug27, a deliberate and reasoned departure from the settings-derived rule.
//
// WHY. Two reasons, and the second is why it is also the SAFE choice:
//   1  The defect is measured in absolute nomination picks — PICK_BUCKETS and
//      MICRO_BUCKETS are both absolute — and a damper keyed on a different axis
//      than its own evidence cannot be gated against that evidence.
//   2  The premium's MECHANISM is budget fullness: early fields are big (8.5-10.4
//      willing bidders vs 4.1-7.5 by pick 41-80) because every seat is still rich.
//      Budget fullness lasts LONGER in bigger rooms, so a fixed 20-pick window
//      under-damps the late part of a 14-team room's premium rather than
//      over-damping its early part. It errs toward the known, tolerated artifact
//      and away from the pick-1 steal that got aug26 retracted — conservative in
//      exactly the direction the retraction says to be conservative.
//
// NAMED REFINEMENT, not a blocker: re-derive on pick÷teams once the corpus
// supports size-split references. It does not today — the micro-bucket cells run
// n~32-33 pooled across room sizes, and splitting by teams would thin them past
// usefulness. The legacy ramp remains settings-derived.
export const _flatCurve = { earlyDamp: 1.0, earlyRounds: 2, shape: null };

/**
 * Is the damper doing anything at all?
 *
 * OFF must stay byte-inert — nothing computed, no checkpoint recorded, no
 * randomness drawn — because the pre-ship pin in softcapProbe.mjs restores the
 * pre-damper engine by setting these fields back, and every legacy artifact's
 * reproduction depends on that restoration being exact. At { earlyDamp: 1,
 * shape: null } this returns false, which is the identical condition the call
 * site tested before `shape` existed.
 */
export function flatCurveActive() {
  return _flatCurve.earlyDamp !== 1 || _flatCurve.shape != null;
}

/**
 * The damper's multiplier for one pick. Returns exactly 1 when the dial is off,
 * outside the early window, or when teams/pickNumber are not usable.
 */
export function flatCurveMult(pickNumber, teams, eliteCount = 0) {
  const sh = _flatCurve.shape;
  if (sh) {
    if (!Number.isFinite(pickNumber) || pickNumber < 1) return 1;
    if (sh.kind === 'constant') {
      return pickNumber <= sh.picks ? sh.damp : 1;
    }
    if (sh.kind === 'steps') {
      for (const [maxPick, m] of sh.steps) if (pickNumber <= maxPick) return m;
      return 1;
    }
    if (sh.kind === 'taper') {
      if (pickNumber <= sh.full) return sh.damp;
      if (pickNumber > sh.end) return 1;
      // The handoff: once the shipped eliteCount ladder engages, stop — this
      // damper exists to cover the window BEFORE that ladder fires, and doubling
      // up would be a second deflation, not a first step.
      if (sh.handoff != null && eliteCount >= sh.handoff) return 1;
      const span = sh.end - sh.full;
      if (!(span > 0)) return 1;
      const t = (pickNumber - sh.full) / span;   // 0 just past `full`, 1 at `end`
      return sh.damp + (1 - sh.damp) * t;
    }
    return 1;   // unknown kind is inert, never a silent partial effect
  }
  const d = _flatCurve.earlyDamp;
  if (d === 1) return 1;
  const w = Math.round(_flatCurve.earlyRounds * (teams || 12));
  if (!(w > 1) || !Number.isFinite(pickNumber) || pickNumber >= w) return 1;
  const t = Math.max(0, (pickNumber - 1) / (w - 1));
  return d + (1 - d) * t;
}

// Seam A's band lookup. Returns null — meaning "use the shipped TIER_MAPS band"
// — with the seam off, in 1QB, off QB, and for QB1-12 at every state. Those four
// nulls are what make the ship byte-inert everywhere outside the superflex tail.
//
// The personal tier sheet's +/-1 shift is PRESERVED rather than dropped: the
// sheet's tier for this player is compared against the 1QB map's tier for the
// same rank, and the difference is applied as a band offset in the tail ladder,
// clamped to its ends. So the ~10% of seats that value a tail QB a tier richer
// still do, and the homer promotion (T4+ up one) still lands, in the ladder the
// seam installs rather than in the one it replaces.
function qbTailBandFor(pos, sflexCount, posRank, playerTier, homerPromote) {
  if (!_qbTail.sflexTierBranch || sflexCount <= 0 || pos !== "QB") return null;
  const rn = parseInt(String(posRank || "").replace(/[A-Z]+/g, ""), 10);
  if (!Number.isFinite(rn) || rn < QB_SFLEX_TAIL[0].min) return null;   // QB1-12: shipped band
  let idx = QB_SFLEX_TAIL.findIndex(b => rn >= b.min && rn <= b.max);
  if (idx < 0) idx = QB_SFLEX_TAIL.length - 1;
  const base = getTierForRank("QB", rn)?.tier;
  if (base && Number.isFinite(playerTier) && playerTier < 90) {
    idx += Math.max(-1, Math.min(1, playerTier - base));
  }
  if (homerPromote) idx -= 1;
  idx = Math.max(0, Math.min(QB_SFLEX_TAIL.length - 1, idx));
  return QB_SFLEX_TAIL[idx];
}

// ── The D3 pin, derived from the format ─────────────────────────────
// The pin is load-bearing — chunk 5 part 5 measured it at 17pp on Gibbs's $70+
// rate — so it cannot stay a 10-team/$200 constant. $44 was three coincident
// readings at that one format; measured across formats they diverge badly:
//
//   teams          8      10      12      14      16     (standard, $200)
//   WR-T2-bottom  $31     $44     $47     $50     $51
//   0.220 x B     $44     $44     $44     $44     $44    <- blind to team count
//   0.786 x WR1   $41     $44     $46     $49     $50    <- ratio is itself
//                                                           a 10-team artifact
//                                                           (0.60 at 8, 0.81 at 16)
//
// So the pin is the BOARD PRICE AT THE BOTTOM OF WR T2. That rank is not a
// magic number: WR T2 is the band the acceptance's WR HOLD rows sit in, and the
// original $46 pin was chosen for exactly this reason — "JSN's board, because he
// is a HOLD row, so his anchor is invariant by construction at every boardTerm".
// Reading the band's last rank off TIER_MAPS keeps that meaning if the bands are
// ever re-cut again.
//
// This is a derivation from format parameters, not from player data: the board
// price at a given rank is a function of team count, budget and roster shape
// (they set `wrDrafted`, `budgetScale` and the renormalization), which is why it
// tracks all three above while the other two readings track at most one.
// This runs inside calcAiMaxBidV2, which is called many times per bid, so the
// O(pool) part — resolving which player holds the band's last rank — is cached
// on the RANK rather than on the price map. The rank depends only on TIER_MAPS,
// so the cache survives a new price map (a re-render, a format switch) and only
// the O(1) map lookup repeats.
let _pinKeyForRank = { rank: null, key: null };
export function derivePin(dynamicPriceMap, startingBudget) {
  if (_softCap.boardPin != null) return _softCap.boardPin;          // explicit override
  const fallback = 0.22 * startingBudget;
  if (!dynamicPriceMap) return fallback;
  const t2 = TIER_MAPS.WR.find(t => t.tier === 2);
  const rank = t2 ? t2.max : 5;
  if (_pinKeyForRank.rank !== rank) {
    let key = null;
    for (const p of UNIQUE_PLAYERS) {
      if (p.pos !== 'WR') continue;
      if (parseRankNum(getEffectivePosRank(p.name, p.posRank) || p.posRank) !== rank) continue;
      key = `${p.name}-${p.pos}`;
      break;
    }
    _pinKeyForRank = { rank, key };
  }
  const price = _pinKeyForRank.key ? dynamicPriceMap.get(_pinKeyForRank.key) : null;
  return price != null ? price : fallback;
}

// Nomination-branch collector — set _nomDiag.data = [] to enable, null to disable.
// pickAiNomination is a ~25-branch waterfall and nothing downstream records WHICH
// branch produced a nomination, so a probe cannot attribute an early-pick pattern
// to (say) tax-the-room vs. the Homer preference. Every return site is wrapped in
// NT(tag, player); when data is null the only cost is one null test per return.
export const _nomDiag = { data: null };

// ── Bid-pipeline trace (diagnostic; OFF by default) ─────────────────
// Set _bidTrace.on = true (optionally _bidTrace.player = "Name" to filter) and
// every calcAiMaxBidV2 call pushes a row: the ladder of maxBid values, one entry
// per pipeline stage that actually MOVED the number, plus the inputs. Lets a
// probe attribute a final bid to the specific gate that produced it instead of
// guessing. When `on` is false the only cost is one boolean test per call.
export const _bidTrace = { on: false, player: null, rows: [], notes: [] };
// Caller-side companion to the row ladder: lets the draft loop record the
// surrounding auction context (opening bid, post-floor field, pre-sale team
// state) for the same nomination. No-op unless tracing is on.
export function _traceNote(obj) { if (_bidTrace.on) _bidTrace.notes.push(obj); }
// `_bidTrace.player` accepts a name, an array of names, or null for "everything".
// The array form exists so a probe can trace a whole cohort (e.g. each position's
// board top 5) in ONE draft instead of re-running the draft once per player.
export function _traceWants(name) {
  if (!_bidTrace.on) return false;
  const filter = _bidTrace.player;
  if (!filter) return true;
  return Array.isArray(filter) ? filter.includes(name) : filter === name;
}
let _tr = null;                       // active trace row, or null
function mk(label, v) {               // checkpoint — records only on change
  if (!_tr) return;
  const r = Math.round(v * 100) / 100;
  const prev = _tr.steps.length ? _tr.steps[_tr.steps.length - 1].v : null;
  if (prev === null || Math.abs(r - prev) > 0.009) _tr.steps.push({ label, v: r });
}
function mkctx(obj) { if (_tr) Object.assign(_tr.ctx, obj); }

export function countSubstitutes(player, pos, playerTier, dynPrice, draftedPlayers) {
  if (!draftedPlayers) return 2;
  return UNIQUE_PLAYERS.filter(p => {
    if (p.pos !== pos) return false;
    if (p.name === player.name) return false;
    if (draftedPlayers.has(p.name)) return false;
    const rank = parseRankNum(getAdpPosRank(p.name) || p.posRank);
    const tierObj = getTierForRank(pos, rank);
    const tierNum = tierObj?.tier ?? 99;
    if (tierNum < playerTier - 1 || tierNum > playerTier + 1) return false;
    const pDyn = dynPrice > 0 ? dynPrice : 1;
    const pVal = p.value || 1;
    if (pVal < pDyn * 0.7 || pVal > pDyn * 1.3) return false;
    return true;
  }).length;
}

export function getSubstituteMultiplier(count) {
  if (count >= 4) return 0.78;
  if (count >= 2) return 0.90;
  if (count === 1) return 1.00;
  return 1.12;
}

// ============================================================
// Settings-derived draftable pool
// ============================================================
// Computes, per position, how many players are worth drafting given the
// league's roster settings and team count. Demand = dedicated starters +
// a proportional share of FLEX/SFLEX slots + a bench stash (weighted by
// real hoarding propensity), then TE/QB depth ceilings pull the shallow
// stream positions down to real rostered depth. A 1.15× roster-slot floor
// (routed to RB/WR only) guarantees benches fill. Validated vs 9 years of
// real auction data across 4 leagues.
function draftablePoolCounts(settings = {}) {
  const teams = settings.teams || 10;
  const P = settings.positions || {};
  const start = { QB: P.QB || 0, RB: P.RB || 0, WR: P.WR || 0, TE: P.TE || 0, DEF: P.DEF ?? 0, K: P.K ?? 0 };
  const flex = P.FLEX || 0, sflex = P.SFLEX || 0, bench = P.BN || 0;
  const flexElig = ["RB", "WR", "TE"], sflexElig = ["QB", "RB", "WR", "TE"];
  const demand = { QB: start.QB, RB: start.RB, WR: start.WR, TE: start.TE, DEF: start.DEF, K: start.K };
  const addShare = (amount, eligible) => {
    if (amount <= 0) return;
    const totalW = eligible.reduce((s, p) => s + start[p], 0);
    for (const p of eligible) demand[p] += totalW > 0 ? amount * (start[p] / totalW) : amount / eligible.length;
  };
  addShare(flex, flexElig); addShare(sflex, sflexElig);
  // Deep benches are hoarded at RB/WR; TE is a stream position in all formats.
  // QB is a stream position at 1QB but a mandatory multi-starter in superflex,
  // so its hoarding propensity is format-aware.
  const benchElig = ["QB", "RB", "WR", "TE"];
  const benchProp = { QB: sflex > 0 ? 1.0 : 0.5, RB: 1.0, WR: 1.0, TE: 0.45 };
  const benchW = benchElig.reduce((s, p) => s + demand[p] * benchProp[p], 0);
  for (const p of benchElig) demand[p] += benchW > 0 ? bench * (demand[p] * benchProp[p] / benchW) : bench / benchElig.length;
  const counts = {};
  for (const p of Object.keys(demand)) counts[p] = Math.ceil(demand[p] * teams);
  for (const p of ["DEF", "K"]) if (counts[p] > 0) counts[p] = Math.ceil(counts[p] * 1.25);
  // Depth ceilings for the shallow stream positions (starters + flex/sflex share + small stash).
  const teFlexShare = flex > 0 && (start.RB + start.WR + start.TE) > 0 ? flex * (start.TE / (start.RB + start.WR + start.TE)) : 0;
  counts.TE = Math.min(counts.TE, Math.ceil(teams * (start.TE + teFlexShare + (sflex > 0 ? 0.15 : 0) + 0.40)));
  // QB depth ceiling only at 1QB — superflex QB is a mandatory 2nd starter, not a stream.
  if (sflex === 0) counts.QB = Math.min(counts.QB, Math.ceil(teams * (start.QB + 0.65)));
  // Roster-floor cushion routes to real bench-depth positions; superflex QB rejoins.
  const rosterSlots = Object.values(P).reduce((s, v) => s + (v || 0), 0);
  const requiredMin = Math.ceil(rosterSlots * teams * 1.15);
  const absorbPos = sflex > 0 ? ["QB", "RB", "WR"] : ["RB", "WR"];
  const totalDraftable = Object.values(counts).reduce((s, v) => s + v, 0);
  if (totalDraftable < requiredMin) {
    const deficit = requiredMin - totalDraftable;
    const absorbTotal = absorbPos.reduce((s, p) => s + counts[p], 0);
    for (const p of absorbPos) counts[p] += Math.ceil(deficit * (absorbTotal > 0 ? counts[p] / absorbTotal : 1 / absorbPos.length));
  }
  return counts;
}

// Is this player within the settings-derived draftable pool for their position?
export function isDraftable(player, settings = {}) {
  if (!player?.pos) return false;
  const counts = draftablePoolCounts(settings);
  const rank = parseRankNum(getEffectivePosRank(player.name, player.posRank));
  return rank <= (counts[player.pos] || 0);
}

// WORP-based scoring format multipliers (vs half-PPR baseline)
// Non-PPR(0): QB 1.10, RB 1.18, WR 0.82, TE 0.79
// Full-PPR(1.0): QB 0.92, RB 0.86, WR 1.14, TE 1.18
// Half-PPR(0.5): all 1.0. Interpolate linearly for any value.
function getScoringMultiplier(pos, ppr) {
  if (pos === "DEF" || pos === "K") return 1.0;
  const halfPPR = 0.5;
  const mults = {
    QB: { zero: 1.10, full: 0.92 },
    RB: { zero: 1.18, full: 0.86 },
    WR: { zero: 0.82, full: 1.14 },
    TE: { zero: 0.79, full: 1.18 },
  };
  const m = mults[pos];
  if (!m) return 1.0;
  if (ppr <= halfPPR) return m.zero + (1.0 - m.zero) * (ppr / halfPPR);
  return 1.0 + (m.full - 1.0) * ((ppr - halfPPR) / halfPPR);
}

// ============================================================
// calcAiMaxBidV2 — Tier-Anchored Bidding Engine
// ============================================================
// Core change: bids are anchored to the PLAYER'S tier value, then
// scaled by team need, archetype willingness, and budget position.
// Replaces the shopping-list-slot-based bidding of V1.
//
// Formula: maxBid = tierValue × needMult × willingness × budgetMod × subMult
//
// Then post-processing: scarcity, superflex, tier anchor, homer,
// elite deflation, inflation, archetype caps, must-fill, surplus floor.

// Trace wrapper — a pass-through unless _bidTrace.on. Argument defaults live on
// the implementation below, so undefined still lands on the same value.
export function calcAiMaxBidV2(profile, player, teamRoster, budget, emptySlots, eliteCount, teamCaps, teamObj, pickNumber, tierAnchors, inflationIndex, allTeams, sflexCount, settings, dynamicPriceMap, draftedPlayers, nominatorId, aiRosterSize) {
  if (!_traceWants(player?.name)) {
    return calcAiMaxBidV2Impl(profile, player, teamRoster, budget, emptySlots, eliteCount, teamCaps, teamObj, pickNumber, tierAnchors, inflationIndex, allTeams, sflexCount, settings, dynamicPriceMap, draftedPlayers, nominatorId, aiRosterSize);
  }
  const outer = _tr;
  _tr = { team: teamObj?.name, archetype: profile?.archetype, modifiers: profile?.modifiers || [], player: player?.name, pick: pickNumber, steps: [], ctx: {} };
  const row = _tr;
  const out = calcAiMaxBidV2Impl(profile, player, teamRoster, budget, emptySlots, eliteCount, teamCaps, teamObj, pickNumber, tierAnchors, inflationIndex, allTeams, sflexCount, settings, dynamicPriceMap, draftedPlayers, nominatorId, aiRosterSize);
  row.final = out;
  _bidTrace.rows.push(row);
  _tr = outer;
  return out;
}

function calcAiMaxBidV2Impl(profile, player, teamRoster, budget, emptySlots, eliteCount, teamCaps, teamObj, pickNumber, tierAnchors, inflationIndex, allTeams, sflexCount = 0, settings = {}, dynamicPriceMap = null, draftedPlayers = null, nominatorId = null, aiRosterSize = MOCK_ROSTER_SIZE) {
  const pos = player.pos;
  const posRank = getAdpPosRank(player.name) || player.posRank;
  const dynPrice = dynamicPriceMap?.get(`${player.name}-${player.pos}`) || player.value;
  // League-calibration rider (attached by usePricing.dynamicPrices): the same
  // per-position multipliers that moved the displayed prices scale every
  // TIER_MAPS-derived valuation below, so the AI's willingness and the shown
  // price move together. Absent (every uncalibrated league, batch sim, the
  // probes) → posMult 1 → byte-identical to before.
  const leagueMult = dynamicPriceMap?.__leagueMultipliers || null;
  const posMult = leagueMult ? (leagueMult[player.pos] ?? 1) : 1;
  // MARKET-ANCHOR rider (attached by usePricing / simConfig.buildDynamicPriceMap
  // via marketAnchorFromPriceMap). Carries the five soft-cap numbers fitted to
  // the user's own answers about what the top of their market clears at. Absent
  // — every league that has not answered, batch sim, every probe — leaves
  // `sc` as `_softCap` itself, so the shipped path is the SAME OBJECT it always
  // read, not a copy of it. Never mutate `_softCap` to carry this (devDials.js
  // does; it is the pattern to avoid — a module global cannot be per-league).
  const anchorSC = dynamicPriceMap?.__marketAnchor || null;
  const startingBudget = settings?.budget || 200;
  const budgetScale = startingBudget / 200;  // archetype $ calibrated for $200
  const caps = teamCaps || {};
  const posCount = teamRoster.filter(p => p.player.pos === pos).length;

  // --- Hard position caps ---
  const kSlots = settings?.positions?.K ?? 1;
  if (pos === "K" && (kSlots === 0 || posCount >= kSlots)) { mk("exit:K-cap", 0); return 0; }
  const defSlots = settings?.positions?.DEF ?? 1;
  if (pos === "DEF" && (defSlots === 0 || posCount >= (caps.maxDef || 1))) { mk("exit:DEF-cap", 0); return 0; }
  let priceEnforceOnly = false;
  if (pos === "QB" && posCount >= (caps.maxQB || (sflexCount > 0 ? 4 : 2))) priceEnforceOnly = true;
  if (pos === "TE" && posCount >= (caps.maxTE || 2)) priceEnforceOnly = true;
  let hardCap = emptySlots <= 1 ? budget : budget - (emptySlots - 1);

  // --- Starter requirements — settings-derived, archetype-aware ---
  // Hoisted above the budget reservation below: four separate tables used to
  // hardcode RB/WR and disagree with each other (:205 said WR:3, :478 said WR:2
  // in the same file). This is now the single source for all of them, so
  // starterOverrides (RB Heavy RB:3, WR Heavy WR:4) finally reach them too.
  const starterReqs = { QB: (settings?.positions?.QB || 1) + sflexCount, RB: settings?.positions?.RB || 2, WR: settings?.positions?.WR || 3, TE: settings?.positions?.TE || 1 };
  Object.entries(profile.starterOverrides || {}).forEach(([p, c]) => { starterReqs[p] = c; });

  // --- Budget reservation for unfilled starter positions ---
  let reservedBudget = 0;
  if (emptySlots >= 4) {
    // Was { QB: settings (no sflex), RB: 2, WR: 2, TE: 1 } — under-reserved WR
    // in every WR:3 league and ignored the SFLEX QB2 requirement.
    const resReqs = starterReqs;
    for (const [rp, req] of Object.entries(resReqs)) {
      if (rp === pos) continue;
      const have = teamRoster.filter(p => p.player.pos === rp).length;
      const need = Math.max(0, req - have);
      if (need > 0) {
        const midTier = TIER_MAPS[rp]?.[Math.min(2, TIER_MAPS[rp].length - 1)];
        const rpMult = leagueMult ? (leagueMult[rp] ?? 1) : 1;
        reservedBudget += need * (midTier ? midTier.floor * startingBudget * rpMult * 0.5 : 5);
      }
    }
  }
  // Homer reserve: carve-out subtracted from non-homer bids, skipped for homer bids
  const biddingOnHomer = !!(profile.favoriteTeam && player.team === profile.favoriteTeam);
  const homerReserve = (!biddingOnHomer && teamObj?.homerReserve > 0) ? teamObj.homerReserve : 0;
  const effectiveBudget = Math.max(emptySlots, budget - reservedBudget - homerReserve);

  // --- Positional scarcity multiplier (league format, unchanged) ---
  const baselineSlots = { QB: 1, RB: 2.5, WR: 3.5, TE: 1 };
  const actualSlotsPerTeam = {
    QB: (settings?.positions?.QB || 1) + sflexCount,
    RB: (settings?.positions?.RB || 2) + (settings?.positions?.FLEX || 1) * 0.5,
    WR: (settings?.positions?.WR || 2) + (settings?.positions?.FLEX || 1) * 0.5,
    TE: (settings?.positions?.TE || 1),
  };
  let scarcityMultiplier = (pos !== "DEF" && pos !== "K") ? actualSlotsPerTeam[pos] / baselineSlots[pos] : 1;

  // SFLEX QB: rank-dependent scarcity curve, dampened by strategy
  // QB1 avg $52 vs RB1 avg $79 — elite QBs valuable but below elite RBs
  if (sflexCount > 0 && pos === "QB" && scarcityMultiplier !== 1) {
    const qbDemand = ((settings?.positions?.QB || 1) + sflexCount) * (settings?.teams || 10);
    const qbBaseline = 1 * 10;
    const demandRatio = qbDemand / qbBaseline;
    const qbRankRaw = parseInt((posRank || "").replace(/[A-Z]+/g, ""), 10) || 99;
    const mods = profile.modifiers || [];
    const dampener = mods.includes("Elite QB SF") ? 1.0
                   : mods.includes("Balanced QB SF") ? _sflexDials.balancedDampener  // 0.7 shipped
                   : mods.includes("Punt QB SF") ? _sflexDials.puntDampener          // 0.4 shipped
                   : 1.0;
    const scale = (qbRankRaw <= 3 ? 0.25
                : qbRankRaw <= 8 ? 0.35
                : qbRankRaw <= 14 ? 0.45
                : 0.55) * dampener;
    scarcityMultiplier = 1.0 + (demandRatio - 1.0) * scale;
  }

  // --- Avoid check (unchanged) ---
  const isAvoided = teamObj?.avoids?.includes(player.name);
  if (isAvoided) {
    const posReqsAvoid = { ...starterReqs, DEF: (settings?.positions?.DEF ?? 1), K: kSlots };
    const stillNeededAvoid = Math.max(0, (posReqsAvoid[pos] || 0) - posCount);
    if (stillNeededAvoid > 0) {
      const countsAvoid = {};
      teamRoster.forEach(p => { countsAvoid[p.player.pos] = (countsAvoid[p.player.pos] || 0) + 1; });
      let totalNeededAvoid = 0;
      for (const [p, req] of Object.entries(posReqsAvoid)) {
        totalNeededAvoid += Math.max(0, req - (countsAvoid[p] || 0));
      }
      if (emptySlots <= totalNeededAvoid + 1) {
        // Fall through — must fill
      } else {
        mk("exit:avoid", 0); return 0;
      }
    } else {
      mk("exit:avoid", 0); return 0;
    }
  }

  // --- DEF/K pricing (unchanged — flat ranges, not tier-anchored) ---
  if (pos === "DEF" || pos === "K") {
    const rn = parseInt((posRank || '').replace(/[A-Z]+/g, ''), 10);
    let maxBid = 1;
    if (pos === "DEF") {
      if (!isNaN(rn) && rn <= 3) {
        maxBid = 2 + Math.floor(Math.random() * 3);
        if (budget > 30 && Math.random() < 0.25) maxBid = 5;
      } else if (!isNaN(rn) && rn <= 8) {
        maxBid = 1 + Math.floor(Math.random() * 2);
      }
    } else {
      if (!isNaN(rn) && rn <= 2) {
        maxBid = 1 + Math.floor(Math.random() * 2);
        if (budget > 20 && Math.random() < 0.20) maxBid = 3;
      }
    }
    const posRequired = pos === "K" ? kSlots : (settings?.positions?.DEF ?? 1);
    if ((pos === "K" || pos === "DEF") && posRequired > 0 && emptySlots <= 5 && posCount < posRequired) {
      maxBid = Math.max(maxBid, Math.min(3, hardCap));
    }
    if (pos === "DEF") maxBid = Math.min(maxBid, 5);
    if (pos === "K") maxBid = Math.min(maxBid, 3);
    return Math.max(0, Math.min(maxBid, hardCap));
  }

  // === TIER-ANCHORED CORE (new in V2) ===

  // 1. Player tier lookup (using personal tier sheet with base fallback)
  const tierSheet = teamObj?.tierSheet;
  const playerTier = tierSheet?.get(player.name) || (() => {
    const rn = parseInt((posRank || '').replace(/[A-Z]+/g, ''), 10);
    return isNaN(rn) ? 99 : (getTierForRank(pos, rn)?.tier || 99);
  })();
  const isHomer = !!(profile.favoriteTeam && player.team === profile.favoriteTeam);
  const tierObj = TIER_MAPS[pos]?.find(t => t.tier === playerTier); // actual tier — used for anchors, subs
  // Homer tier promotion: T4+ players promoted 1 tier (T4→T3, T5→T4, etc.)
  // T1-T3 already elite — multiplier only, no tier promotion (prevents $50+ inflation)
  const valueTierObj = (isHomer && playerTier >= 4)
    ? (TIER_MAPS[pos]?.find(t => t.tier === playerTier - 1) || tierObj)
    : tierObj;

  // Seam A (_qbTail.sflexTierBranch, superflex only, SHIPPED aug22). Replaces
  // the floor/ceiling PAIR for QB13+ and nothing else: `playerTier` and
  // `tierObj` above are untouched, so countSubstitutes, the tierAnchors key, the
  // shopping lists and every `playerTier <=` test below read exactly what they
  // read before the ship. `qbBand` is null outside the superflex QB tail.
  const qbBand = qbTailBandFor(pos, sflexCount, posRank, playerTier, isHomer && playerTier >= 4);
  const anchorBand = qbBand || valueTierObj;
  const tierFloor = (anchorBand ? anchorBand.floor * startingBudget : 1) * posMult;
  const tierCeil = (anchorBand ? anchorBand.ceiling * startingBudget : 1) * posMult;

  // 2. Tier value — random point within the tier's price range
  //    Use 40-100% of range (same variance tightening as V1)
  const ppr = settings?.ppr ?? settings?.scoring?.receptions ?? 0.5;
  // Depth scaling: compress tier values for deep rosters (more starters per $)
  // Capped at 1.0 so it never boosts — only compresses for formats like Kings (10 starters)
  const sPos = settings?.positions || {};
  const skillStarters = (sPos.QB || 1) + (sPos.RB || 2) + (sPos.WR || 3) + (sPos.TE || 1) + (sPos.FLEX || 1) + (sPos.SFLEX || 0);
  const skillBudget = startingBudget - ((sPos.K ?? 1) > 0 ? 2 : 0) - ((sPos.DEF ?? 1) > 0 ? 2 : 0);
  const depthScale = Math.min(1.0, Math.pow((skillBudget / skillStarters) / 24.5, 2.0));
  let tierValue = (tierFloor + (0.25 + Math.random() * 0.75) * (tierCeil - tierFloor)) * getScoringMultiplier(pos, ppr) * depthScale;

  // Per-agent CV noise — each AI values each player differently
  tierValue *= (teamObj?.noiseMap?.[player.name] || 1.0);
  mk("tierValue", tierValue);
  mkctx({ playerTier, tierFloor, tierCeil, dynPrice, budget, emptySlots, posCount, hardCap,
          noise: teamObj?.noiseMap?.[player.name] || 1.0, isHomer, heroRBDrafted: !!teamObj?.heroRBDrafted,
          rbCap: profile.rbCap, rbSpendCap: profile.rbSpendCap, heroRB: !!profile.heroRB });

  // Homer budget reserve — homer bids use full effectiveBudget (reserve not subtracted above)
  // No hardCap override needed; the carve-out already makes reserve available for homer bids

  // 3. Need intensity — how badly does this team need this position?
  // (starterReqs is built near the top, above the budget-reservation block)
  const startersNeeded = Math.max(0, (starterReqs[pos] || 0) - posCount);

  // FLEX awareness: count FLEX as real starter need
  let flexNeed = 0;
  if ((pos === "RB" || pos === "WR" || pos === "TE") && startersNeeded === 0) {
    const baseCount = settings?.positions?.[pos] || 0;
    const overrideCount = profile.starterOverrides?.[pos];
    // Skip FLEX need if starterOverride already claimed the FLEX slot
    if (overrideCount === undefined || overrideCount <= baseCount) {
      const flexCount = settings?.positions?.FLEX || 0;
      if (flexCount > 0) {
        const flexEligible = ["RB", "WR", "TE"];
        let flexFilled = 0;
        for (const fp of flexEligible) {
          const dedicated = settings?.positions?.[fp] || 0;
          const have = teamRoster.filter(p => p.player.pos === fp).length;
          flexFilled += Math.max(0, have - dedicated);
        }
        flexNeed = Math.max(0, flexCount - flexFilled);
      }
    }
  }

  // For 3+ FLEX formats, unfilled FLEX slots are valued like real starters
  // First 2 unfilled FLEX count as full starter need, third at reduced weight
  let effectiveFlexNeed = flexNeed;
  if (flexNeed > 0 && (settings?.positions?.FLEX || 0) >= 3) {
    effectiveFlexNeed = Math.min(flexNeed, 2) + Math.max(0, flexNeed - 2) * 0.5;
  }
  const effectiveNeed = startersNeeded + effectiveFlexNeed;
  const needMult = effectiveNeed >= 4 ? 1.30
                 : effectiveNeed >= 3 ? 1.20
                 : effectiveNeed >= 2 ? 1.12
                 : effectiveNeed >= 1 ? 1.05
                 : 0.70;                       // bench

  // 4. Archetype willingness
  const getWillingness = () => {
    // Stars & Scrubs: phase-dependent
    if (profile.starsMode) {
      const starPicks = teamRoster.filter(p => p.price >= 40);
      const starCount = starPicks.length;
      if (starCount >= 3) {
        // Post-stars: aggressive starter fill, moderate bench
        return startersNeeded > 0 ? 1.00 : 0.80;
      }
      // Pre-stars: only interested in RB/WR star candidates
      if (pos === "RB" || pos === "WR") {
        if (playerTier <= 3) return 1.50;  // star candidate
        if (playerTier <= 5 && startersNeeded > 0) return 0.70;  // opportunistic buy
        return 0.30;  // non-star RB/WR fill
      }
      if (playerTier <= 5 && startersNeeded > 0) return 0.70;  // opportunistic QB/TE
      return 0.15;  // QB/TE during star-hunting
    }

    // Hero RB: phase-dependent
    if (profile.heroRB) {
      if (!teamObj?.heroRBDrafted) {
        // Pre-hero: all-in on RBs
        if (pos === "RB") return playerTier <= 3 ? 1.80 : 0.50;
        return 0.20;  // everything else during hero-hunting
      }
      // Post-hero: shift to WR focus
      const postHero = { RB: 0.20, WR: 1.25, QB: 0.70, TE: 0.50 };
      return postHero[pos] || 1.0;
    }

    // Standard archetypes: use posWillingness from profile
    const w = profile.posWillingness?.[pos];
    if (w !== undefined) return w;

    // Fallback defaults by archetype
    const defaults = {
      "RB Heavy":     { RB: 1.15, WR: 0.90, QB: 0.60, TE: 0.80 },
      "WR Heavy":     { RB: 0.90, WR: 1.15, QB: 0.60, TE: 0.60 },
      "Balanced":     { RB: 1.00, WR: 1.00, QB: 1.00, TE: 1.00 },
      "Zero RB":      { RB: 0.40, WR: 1.15, QB: 1.05, TE: 0.90 },
      "The Hammer": { RB: 0.95, WR: 0.95, QB: 0.95, TE: 0.95 },
    };
    return defaults[profile.archetype]?.[pos] || 1.0;
  };
  let willingness = getWillingness();

  // Patient Spender: discount gate — only bid when price is below tier value threshold
  // Uses maxBid = 0 (not return 0) so must-fill and surplus floor can still override downstream
  // Replaced blanket return 0 which bypassed all safety nets (2026-03-15)


  // Format-driven RB/WR willingness rebalance
  // Real data shows RBs hold value better than WRs as QB demand rises.
  // In SFLEX: RB spend 41% vs WR 32% (vs baseline 38%/40%).
  // This reflects steeper RB WAR curves — elite RBs are scarcer.
  if (sflexCount > 0) {
    if (pos === "RB") willingness *= 1.08;
    if (pos === "WR") willingness *= 0.80;
    // SFLEX: strategy-aware QB willingness override (all AI teams have a QB modifier)
    if (pos === "QB") {
      const mods = profile.modifiers || [];
      if (mods.includes("Elite QB SF")) willingness = 1.30;
      else if (mods.includes("Balanced QB SF")) willingness = 1.00;
      else if (mods.includes("Punt QB SF")) willingness = _sflexDials.puntWillingness;  // 0.85 shipped
      // QB2 urgency floor: when team has 1 QB, enforce strategy-aware minimum
      if (posCount === 1 && startersNeeded > 0) {
        if (mods.includes("Elite QB SF")) willingness = Math.max(willingness, 1.00);
        else if (mods.includes("Balanced QB SF")) willingness = Math.max(willingness, 0.85);
        else if (mods.includes("Punt QB SF")) willingness = Math.max(willingness, 0.70);
      }
    }
  }

  // 1QB: Elite QB modifier boosts QB willingness (softened from 1.30 to 1.20)
  if (sflexCount === 0 && pos === "QB") {
    const mods = profile.modifiers || [];
    if (mods.includes("Elite QB")) {
      willingness = Math.max(willingness, 1.20);
    }
    // Punt QB: cap willingness at 0.35 — deprioritize ALL QB tiers, not just T1-2
    if (mods.includes("Punt QB")) {
      willingness = Math.min(willingness, 0.35);
    }
  }

  // TE willingness overrides — modifier-driven, all formats
  if (pos === "TE") {
    const mods = profile.modifiers || [];
    if (mods.includes("Elite TE")) {
      willingness = Math.max(willingness, 1.50);
    }
    if (mods.includes("Punt TE")) {
      willingness = Math.min(willingness, 0.25);
    }
  }

  // Large-league QB dampener: 14+ teams = 1QB per team, no scarcity
  if (pos === "QB" && sflexCount === 0 && (settings?.teams || 10) >= 13) {
    willingness *= 0.60;
  }

  // 5. Budget guardrail — soft positional spending limits
  const posKey = pos.toLowerCase() + "$";
  let posBudget = Math.round((profile[posKey] || 0) * budgetScale);
  // Non-Elite-TE teams: cap TE budget at $20 so surplus can't drive isElite or budgetMod above target
  if (pos === "TE" && !(profile.modifiers || []).includes("Elite TE")) {
    posBudget = Math.min(posBudget, Math.round(20 * budgetScale));
  }

  // SFLEX: strategy-aware QB budget floor (all AI teams have a QB modifier)
  if (sflexCount > 0 && pos === "QB") {
    const mods = profile.modifiers || [];
    let qbFloor = 0;
    if (mods.includes("Elite QB SF")) qbFloor = 0.28;
    else if (mods.includes("Balanced QB SF")) qbFloor = 0.19;
    else if (mods.includes("Punt QB SF")) qbFloor = _sflexDials.puntQbFloor;  // 0.12 shipped
    if (qbFloor > 0) posBudget = Math.max(posBudget, Math.round(startingBudget * qbFloor));
  }

  const posSpent = teamRoster.filter(p => p.player.pos === pos).reduce((s, p) => s + p.price, 0);

  let budgetMod = 1.0;
  if (posBudget > 0) {
    const budgetRatio = posSpent / posBudget;
    // Punt strategy (tiny budget < $15): hard cutoff — return 0 once budget is spent
    if (posBudget < 15 && posSpent >= posBudget) { mk("exit:punt-posBudget-spent", 0); return 0; }
    // Elite strategy (QB budget > $35): stay aggressive up to 1.5x spend
    const isElite = (pos === "QB" || pos === "TE") && posBudget > 35;
    if (isElite) {
      if (budgetRatio > 1.5) budgetMod = 0.30;
      else if (budgetRatio > 1.0) budgetMod = 0.70;
      else if (budgetRatio < 0.5 && effectiveNeed > 0) budgetMod = 1.15;
    } else {
      if (budgetRatio > 1.2) budgetMod = 0.30;
      else if (budgetRatio > 1.0) budgetMod = 0.70;
      else if (budgetRatio < 0.5 && effectiveNeed > 0) budgetMod = 1.15;
      else if (budgetRatio < 0.3) budgetMod = 1.10;
    }
  }

  // 6. Substitute availability modifier (from earlier commits)
  // (Uses countSubstitutes + getSubstituteMultiplier if draftedPlayers available)
  let subMult = 1.0;
  if (draftedPlayers && tierObj) {
    const subs = countSubstitutes(player, pos, playerTier, dynPrice, draftedPlayers);
    subMult = getSubstituteMultiplier(subs);
  }
  // Cap subMult when position already over budget — scarcity shouldn't override the spending plan
  if (posBudget > 0 && posSpent >= posBudget) subMult = Math.min(subMult, 0.90);

  // 7. Starter lockout — block bench buys when required starters unfilled
  // Was { RB: 2, WR: 2, TE: 1 } hardcoded — WR:2 undercounted unfilledStarters
  // by one in every WR:3 league, releasing the bench-buy block a slot early and
  // letting teams finish with an empty WR3. DEF/K handling unchanged.
  const posReqsLock = { ...starterReqs,
    DEF: (settings?.positions?.DEF ?? 1),
    ...(kSlots > 0 ? { K: kSlots } : {}),
  };
  const lockCounts = {};
  teamRoster.forEach(r => { lockCounts[r.player.pos] = (lockCounts[r.player.pos] || 0) + 1; });
  let unfilledStarters = 0;
  for (const [p, req] of Object.entries(posReqsLock)) {
    unfilledStarters += Math.max(0, req - (lockCounts[p] || 0));
  }
  const isBenchPick = effectiveNeed === 0;
  if (isBenchPick && unfilledStarters > 0 && emptySlots <= unfilledStarters + 3) {
    mkctx({ effectiveNeed, unfilledStarters, needMult, willingness, budgetMod, subMult, posBudget, posSpent });
    mk("exit:starter-lockout", 0); return 0;
  }

  // === COMBINE CORE BID ===
  let maxBid = tierValue * needMult * willingness * budgetMod * subMult;
  mkctx({ effectiveNeed, startersNeeded, flexNeed, unfilledStarters, needMult, willingness, budgetMod, subMult, posBudget, posSpent, scarcityMultiplier, effectiveBudget, reservedBudget });
  mk("core", maxBid);

  // Patient Spender: 4-phase graduated discount gate
  // Phase 1 (1-15):  55% — max patience, build war chest
  // Phase 2 (16-30): 72% — start engaging on value
  // Phase 3 (31-50): 85% — selective buying, near full bids
  // Phase 4 (51+):   no cap, full deployment
  if (profile.modifiers?.includes("Patient Spender")) {
    // An explicit positional commitment outranks the hoard AT THAT POSITION ONLY.
    // Patient Spender + Elite TE contradicted each other: capped at 55% of
    // tierValue, an Elite TE team could not bid past $22 on a $40-tierValue TE1
    // nominated in the first 15 picks, lost the player it is built around to a
    // team with no TE commitment, then spent the same budget on a lesser TE in
    // Phase 4. Only Elite TE / Elite QB / Elite QB SF qualify — every other
    // position still hoards on the normal schedule, and the deployment phases
    // and posOverspendCap below are untouched.
    const psMods = profile.modifiers || [];
    const committedHere = (pos === "TE" && psMods.includes("Elite TE")) ||
                          (pos === "QB" && (psMods.includes("Elite QB") || psMods.includes("Elite QB SF")));
    const psCap = committedHere ? 0
               : pickNumber <= 15 ? 0.55
               : pickNumber <= 30 ? 0.72
               : pickNumber <= 50 ? 0.85
               : 0;
    if (psCap > 0) {
      const discountCap = Math.round(tierValue * psCap);
      if (maxBid > discountCap) maxBid = discountCap;
    }
    mk("ps-discount-gate", maxBid);
  }

  // Patient Spender: positional overspend guard — prevent war chest dump on single position
  // e.g. don't blow $58 on TE when posBudget target is $16
  if (profile.modifiers?.includes("Patient Spender") && posBudget > 0) {
    const posOverspendCap = Math.max(0, Math.round(1.5 * posBudget - posSpent));
    if (maxBid > posOverspendCap) maxBid = posOverspendCap;
    mk("ps-posOverspend-guard", maxBid);
  }

  // Patient Spender: graduated war chest deployment
  // Phase A (pick 40+): early deployment 1.20× when budget > 15% starting and $/slot > league avg
  // Phase B (pick 50+): strong deployment 1.50× when $/slot > 1.25× league avg
  // Both phases stack — a PS team at pick 55 with big surplus gets both boosts
  if (profile.modifiers?.includes("Patient Spender") && pickNumber > 40 && maxBid > 0 && allTeams) {
    const myPerSlot = emptySlots > 0 ? budget / emptySlots : 0;
    let totalBudget = 0, totalSlots = 0;
    for (const t of Object.values(allTeams)) {
      const tSlots = aiRosterSize - t.roster.length;
      if (tSlots > 0) { totalBudget += t.budget; totalSlots += tSlots; }
    }
    const avgPerSlot = totalSlots > 0 ? totalBudget / totalSlots : 0;
    // Early deployment: mild pressure starting at pick 40
    if (avgPerSlot > 0 && myPerSlot > avgPerSlot && budget > startingBudget * 0.15) {
      maxBid = Math.round(Math.min(maxBid * 1.20, tierValue * 1.5));
    }
    // Strong deployment: aggressive spending at pick 50+
    if (pickNumber > 50 && avgPerSlot > 0 && myPerSlot > avgPerSlot * 1.25) {
      maxBid = Math.round(Math.min(maxBid * 1.50, tierValue * 1.5));
    }
    mk("ps-deployment", maxBid);
  }

  // === BID COMMITMENT CEILING — price-scaled dropout (tier-aware) ===
  if (maxBid > 35) {
    const r = Math.random();
    let cap;
    if (maxBid > 55) {
      if (playerTier <= 2) {
        // Elite targets (T1-2): bidders stay committed — 85% retention floor
        cap = r < 0.40 ? (0.85 + Math.random() * 0.10) : r < 0.75 ? (0.90 + Math.random() * 0.10) : 1.0;
      } else {
        cap = r < 0.75 ? (0.60 + Math.random() * 0.15) : r < 0.93 ? (0.75 + Math.random() * 0.12) : 1.0;
      }
    } else if (maxBid > 45) {
      cap = r < 0.45 ? (0.65 + Math.random() * 0.10) : r < 0.75 ? (0.78 + Math.random() * 0.10) : 1.0;
    } else {
      cap = r < 0.50 ? (0.85 + Math.random() * 0.10) : r < 0.80 ? (0.90 + Math.random() * 0.08) : 1.0;
    }
    maxBid *= cap;
    mk("commitment-ceiling-dropout", maxBid);
  }

  // === POST-PROCESSING (mostly unchanged from V1) ===

  // Positional scarcity multiplier (league format)
  if (scarcityMultiplier !== 1) {
    maxBid *= scarcityMultiplier;
    mk("scarcity", maxBid);
  }

  // SFLEX QB spend cap (moved from removed sflexBoost block)
  if (sflexCount > 0 && pos === "QB") {
    const qbSpent = teamRoster.filter(p => p.player.pos === "QB").reduce((s, p) => s + p.price, 0);
    if (qbSpent > budget * 0.40 && posCount >= 1) {
      maxBid = Math.min(maxBid, 5);
    }
  }

  // Value floor: elite players get bid up even at non-priority positions
  if (playerTier <= 3 && budget > 40 && emptySlots >= 4) {
    const valueFloor = tierFloor * 0.8;
    if (maxBid < valueFloor) maxBid = valueFloor;
    mk("elite-value-floor", maxBid);
  }

  // Tier anchor adjustment: first sale in a tier anchors subsequent bids
  if (tierAnchors) {
    const anchor = tierAnchors[pos]?.[playerTier];
    if (anchor !== undefined && tierObj) {
      // Seam A rides here too, so the anchor RATIO is measured against the same
      // ladder the bid was drawn from. Without it a raised tail bid would still
      // be scaled by the 1QB midpoint and the two halves would disagree. (The
      // ratio caps at 1.20 and already pins there for tail QBs at HEAD, so this
      // is a consistency fix, not a second lever.)
      const midBand = qbBand || tierObj;
      const tierMid = (midBand.floor + midBand.ceiling) / 2 * startingBudget * posMult;
      const anchorRatio = Math.min(anchor / tierMid, 1.20);
      maxBid *= anchorRatio;
      mkctx({ tierAnchor: anchor, tierMid, anchorRatio });
      mk("tier-anchor", maxBid);
    }
  }

  // Homer bonus — loyalty impulse always fires regardless of positional budget
  // Self-nom: aggressive reach (1.60-1.75×); other-nom: meaningful but lower (1.30-1.45×)
  if (isHomer) {
    const isSelfNom = nominatorId !== null && allTeams?.[nominatorId]?.name === teamObj?.name;
    if (isSelfNom) {
      maxBid *= (1.60 + Math.random() * 0.15);
    } else {
      maxBid *= (1.30 + Math.random() * 0.15);
    }
    mk("homer-bonus", maxBid);
  }

  // Late-draft deflation when elite players are gone
  if (eliteCount >= 8) maxBid *= 0.75;
  else if (eliteCount >= 6) maxBid *= 0.85;
  mk("elite-gone-deflation", maxBid);

  // THE FLAT-CURVE EARLY DAMPER — the missing FIRST step of the ladder directly
  // above. SHIPPED aug26 at earlyDamp 0.60, all formats. Still byte-inert at 1.0:
  // with earlyDamp === 1 the guard is false, nothing is computed, no checkpoint
  // is recorded and no randomness is drawn — which is what lets softcapProbe's
  // FLATCURVE_PRESHIP pin restore the pre-ship engine exactly.
  // Placed here on purpose — same site, same multiplicative form, and
  // upstream of must-fill, so `isMustFill` still overrides it exactly as it
  // overrides the eliteCount steps. See `_flatCurve` for the attribution that
  // chose this site.
  if (flatCurveActive()) {
    // eliteCount is passed so a `taper` shape can hand off to the eliteCount
    // ladder immediately above rather than ending at an arbitrary pick.
    maxBid *= flatCurveMult(pickNumber, settings?.teams, eliteCount);
    mk("flatcurve-early-damp", maxBid);
  }

  // Positional budget guardrail — hard cap on position spending
  // Allow surplus overflow when team has excess budget
  let posRemaining = Math.max(0, posBudget - posSpent);
  if (teamRoster.length >= 3) {
    const posCnts = {};
    const posSpd = {};
    teamRoster.forEach(r => {
      posCnts[r.player.pos] = (posCnts[r.player.pos] || 0) + 1;
      posSpd[r.player.pos] = (posSpd[r.player.pos] || 0) + r.price;
    });
    let committedRemaining = 0;
    for (const [p, req] of Object.entries(starterReqs)) {
      if ((posCnts[p] || 0) < req) {
        const pk = p.toLowerCase() + "$";
        const pBudget = profile[pk] || 0;
        committedRemaining += Math.max(0, pBudget - (posSpd[p] || 0));
      }
    }
    const surplusBudget = budget - committedRemaining;
    if (surplusBudget > 15) {
      if (surplusBudget > 40 && posCount === 0 && (starterReqs[pos] || 0) >= 1) {
        posRemaining += surplusBudget * 0.9;
      } else {
        const overflowRate = (sflexCount > 0 && pos === "QB") ? 0.8 : 0.7;
        posRemaining += Math.min(surplusBudget * overflowRate, 80);
      }
    }
  }
  // Non-Elite-TE teams: cap posRemaining at $20 — surplus overflow can't fund overbids on TE
  if (pos === "TE" && !(profile.modifiers || []).includes("Elite TE")) {
    posRemaining = Math.min(posRemaining, Math.round(20 * budgetScale));
  }
  const surplusBudget2 = effectiveBudget - emptySlots;
  const hasSurplus = surplusBudget2 > 30;
  if (hasSurplus) {
    const overflowFactor = (sflexCount > 0 && pos === "QB") ? 0.5 : 0.3;
    maxBid = Math.min(maxBid, posRemaining + surplusBudget2 * overflowFactor, hardCap);
  } else {
    maxBid = Math.min(maxBid, posRemaining, hardCap);
  }
  mkctx({ posRemaining, surplusBudget2, hasSurplus });
  mk("posBudget-guard", maxBid);

  // Live inflation adjustment
  if (inflationIndex) {
    if (inflationIndex > 30) {
      const isSmartAI = profile.archetype === "Balanced" || profile.archetype === "The Hammer";
      const tighten = isSmartAI ? (0.90 + Math.random() * 0.05) : (0.95 + Math.random() * 0.02);
      maxBid *= tighten;
    } else if (inflationIndex < -30) {
      maxBid *= (1.05 + Math.random() * 0.05);
    }
    mk("live-inflation", maxBid);
  }

  // === Archetype-specific caps ===
  // Zero RB: max $10 on any single RB
  if (profile.rbCap < 999 && pos === "RB") {
    maxBid = Math.min(maxBid, profile.rbCap);
    mk("rbCap", maxBid);
  }
  // The Hammer: bid intensity scales with rival budget pressure
  if (profile.hammerMode && allTeams) {
    const myName = teamObj?.name;
    if (_hammerArm.trueHammer && sflexCount > 0) {
      // ARM C — the hammer as a RANK, re-evaluated on every bid. Nothing is
      // stored, so possession PASSING is emergent: the moment a big purchase
      // drops this seat's capacity below a rival's, the next call reads
      // hammerHeld false and the seat is back to accumulating.
      // `hardCap` (:485) IS `budget - (emptySlots - 1)` already, so myCap needs
      // no separate derivation; rivals use the same expression getOpponentMaxBid
      // and the endgame scan use.
      let topRivalCap = 0;
      for (const t of Object.values(allTeams)) {
        if (t.name === myName) continue;
        const tSlots = aiRosterSize - t.roster.length;
        if (tSlots <= 0) continue;
        const c = tSlots <= 1 ? t.budget : t.budget - (tSlots - 1);
        if (c > topRivalCap) topRivalCap = c;
      }
      const hammerHeld = hardCap > topRivalCap;
      // Settings-derived, not the ×16 hardcode at :1159 — the rank comparison
      // self-scales to every format and the phase boundary must too.
      const hTotalPicks = (settings?.teams || 10) * aiRosterSize;
      if (hammerHeld) {
        // SWING. No dampening at all; a boost only on the tiers this seat is
        // actually shopping for, bounded by the tier ceiling. Bounded UPWARD
        // only — Math.max keeps the ceiling from acting as a stealth cap on a
        // bid that legitimately already exceeded it upstream.
        const sl = profile.shoppingList;
        let inTargetTiers = false;
        if (sl) {
          for (const [slotKey, slotDef] of Object.entries(sl)) {
            const slotPos = slotDef.pos === "ANY" ? null : (slotDef.pos || slotKey.replace(/[0-9]/g, ''));
            if (slotPos !== pos) continue;
            const slotNum = parseInt(slotKey.replace(/[A-Z]+/g, ''), 10) || 1;
            if (slotKey === "FLEX") {
              if (posCount > (starterReqs[pos] || 1)) continue;
            } else if (posCount >= slotNum) continue;
            if ((slotDef.tiers || []).includes(playerTier)) { inTargetTiers = true; break; }
          }
        }
        if (inTargetTiers) maxBid = Math.max(maxBid, Math.min(maxBid * _hammerArm.swingMult, tierCeil));
      } else if (pickNumber < 0.5 * hTotalPicks) {
        // ACCUMULATE — bank toward taking the hammer.
        maxBid *= _hammerArm.accumDampen;
      }
      // Late and not holding: fall through untouched, i.e. behave as Balanced.
      // Never strand waiting for a hammer that is not coming.
    } else {
      const rivalRichTeams = Object.values(allTeams).filter(t => t.name !== myName && t.budget > 80).length;
      if (rivalRichTeams >= 6) maxBid *= 0.55;
      else if (rivalRichTeams >= 4) maxBid *= 0.80;
      else if (rivalRichTeams >= 2) maxBid *= 1.00;
      else {
        const surpPerSlot = budget / Math.max(1, emptySlots);
        if (surpPerSlot > 4 && effectiveNeed > 0) {
          maxBid = Math.max(maxBid, Math.min(Math.round(surpPerSlot * 1.3), hardCap));
        } else {
          maxBid *= 1.10;
        }
      }
    }
    mk("hammer", maxBid);
  }
  // Cumulative RB spending cap (WR Heavy: $65 max total)
  if (profile.rbSpendCap > 0 && pos === "RB") {
    const rbSpent = teamRoster.filter(p => p.player.pos === "RB").reduce((s, p) => s + p.price, 0);
    if (rbSpent >= profile.rbSpendCap) maxBid = Math.min(maxBid, 1);
    else maxBid = Math.min(maxBid, profile.rbSpendCap - rbSpent);
    mkctx({ rbSpent, rbSpendCapExhausted: rbSpent >= profile.rbSpendCap });
    mk(rbSpent >= profile.rbSpendCap ? "rbSpendCap-exhausted" : "rbSpendCap-remaining", maxBid);
  }
  // Balanced: overspend guardrail + $40 single-player cap
  if (profile.archetype === "Balanced") {
    if (pos === "RB" || pos === "WR") {
      const spent = teamRoster.filter(p => p.player.pos === pos).reduce((s, p) => s + p.price, 0);
      const target = Math.round((profile[posKey] || 0) * budgetScale);
      if (target > 0 && spent > target * 1.25) {
        if (emptySlots <= 6 && budget > emptySlots * 5) {
          maxBid = Math.min(maxBid, Math.max(3, Math.floor(budget / emptySlots * 0.5)));
        } else {
          maxBid = Math.min(maxBid, 1);
        }
        mk("balanced-overspend-guardrail", maxBid);
      }
    }
    maxBid = Math.min(maxBid, 40);
    mk("balanced-$40-clamp", maxBid);
  }
  // QB2 cap in 1QB formats
  const qbSpentForCap = (pos === "QB" && sflexCount === 0 && posCount >= 1)
    ? teamRoster.filter(p => p.player.pos === "QB").reduce((s, p) => s + p.price, 0) : 0;
  if (pos === "QB" && sflexCount === 0 && posCount >= 1) {
    const mods = profile.modifiers || [];
    if (mods.includes("Elite QB")) {
      // Stud + cheap flier — one premium QB, QB2 never expensive
      maxBid = Math.min(maxBid, 5);
    } else if (mods.includes("Punt QB") || qbSpentForCap >= 25) {
      maxBid = Math.min(maxBid, 1);
    }
  }
  // Hero RB: max $5 on RBs after hero drafted
  if (profile.heroRB && pos === "RB" && teamObj?.heroRBDrafted) {
    maxBid = Math.min(maxBid, 5);
    mk("heroRB-post-hero-$5", maxBid);
  }

  // My Guys / Sleepers boost: apply stored per-player boost
  // Skip boost for QB2-capped teams — cap must hold regardless of My Guy status
  const isQb2CappedEarly = pos === "QB" && sflexCount === 0 && posCount >= 1 &&
    ((profile.modifiers || []).includes("Elite QB") || (profile.modifiers || []).includes("Punt QB") || qbSpentForCap >= 25);
  const myGuyEntry = teamObj?.myGuys?.find(g => g.name === player.name);
  const sleeperEntry = teamObj?.sleepers?.find(g => g.name === player.name);
  const isMyGuy = !!myGuyEntry;
  if (myGuyEntry && maxBid > 0 && !isQb2CappedEarly) {
    maxBid *= myGuyEntry.boost;
    mk("myGuy-boost", maxBid);
  } else if (sleeperEntry && !isQb2CappedEarly) {
    maxBid = Math.max(maxBid * sleeperEntry.boost, 3);
    mk("sleeper-boost", maxBid);
  }

  // Homer budget guardrail
  if (isHomer && posKey) {
    const homerSpent = teamRoster.filter(p => p.player.pos === pos).reduce((s, p) => s + p.price, 0);
    const homerTarget = Math.round((profile[posKey] || 0) * budgetScale);
    if (homerTarget > 0 && homerSpent >= homerTarget + 10) {
      if (emptySlots <= 6 && budget > emptySlots * 5) {
        maxBid = Math.max(3, Math.floor(budget / emptySlots * 0.5));
      } else {
        maxBid = 0;
      }
    } else if (homerTarget > 0 && homerSpent + maxBid > homerTarget + 10) {
      if (emptySlots <= 6 && budget > emptySlots * 5) {
        maxBid = Math.min(maxBid, Math.max(3, Math.floor(budget / emptySlots * 0.5)));
      } else {
        maxBid = Math.max(0, homerTarget + 10 - homerSpent);
      }
    }
    mk("homer-budget-guardrail", maxBid);
  }

  // === Endgame intelligence (picks 81+) ===
  if (pickNumber > 80 && allTeams && maxBid > 0) {
    const myId = teamObj?.name;
    let highestRivalMaxBid = 0;
    Object.values(allTeams).forEach(t => {
      if (t.name === myId) return;
      const tSlots = aiRosterSize - t.roster.length;
      if (tSlots <= 0) return;
      const tMaxBid = tSlots <= 1 ? t.budget : t.budget - (tSlots - 1);
      if (tMaxBid > highestRivalMaxBid) highestRivalMaxBid = tMaxBid;
    });
    // Reserve budget for unfilled positions
    if (!isMyGuy) {
      const myPosCnts = {};
      teamRoster.forEach(r => { myPosCnts[r.player.pos] = (myPosCnts[r.player.pos] || 0) + 1; });
      let reserveForLater = 0;
      for (const [rPos, req] of Object.entries(starterReqs)) {
        if ((myPosCnts[rPos] || 0) >= req) continue;
        if (rPos === pos) continue;
        // Reserve the tier floor for a mid-tier player at that position
        const midTier = TIER_MAPS[rPos]?.[Math.floor(TIER_MAPS[rPos].length / 2)];
        const rPosMult = leagueMult ? (leagueMult[rPos] ?? 1) : 1;
        if (midTier) reserveForLater += midTier.floor * startingBudget * rPosMult;
      }
      reserveForLater += Math.max(0, emptySlots - 1);
      const endgameCap = Math.max(0, budget - reserveForLater);
      if (endgameCap < maxBid) maxBid = endgameCap;
      mk("endgame-reserve", maxBid);
    }
    // My Guy timing discipline
    if (isMyGuy && highestRivalMaxBid >= maxBid && emptySlots > 3) {
      maxBid = Math.min(maxBid, hardCap * 0.85);
    }
  }

  // === Must-fill safety net ===
  // Hard cap: 2 QBs max in 1QB, 3+sflexCount in SFLEX
  const posLimits = { QB: sflexCount > 0 ? 3 + sflexCount : 2, RB: 99, WR: 99, TE: 3 };
  if (posCount >= (posLimits[pos] || 3)) { maxBid = 0; mk("hard-pos-limit", maxBid); }
  const posReqs = { QB: (settings?.positions?.QB || 1) + sflexCount, RB: 2, WR: 3, TE: 1, DEF: 1, K: kSlots };
  const stillNeeded = Math.max(0, (posReqs[pos] || 0) - posCount);
  let isMustFill = false;
  if (stillNeeded > 0 && maxBid <= 0) {
    const counts = {};
    teamRoster.forEach(p => { counts[p.player.pos] = (counts[p.player.pos] || 0) + 1; });
    let totalNeeded = 0;
    for (const [p, req] of Object.entries(posReqs)) {
      totalNeeded += Math.max(0, req - (counts[p] || 0));
    }
    if (emptySlots <= totalNeeded + 4) {
      maxBid = hardCap;
      isMustFill = true;
    } else if (pickNumber > 80 && playerTier <= 5 && budget > 15) {
      const tierSpike = playerTier <= 3 ? _sflexDials.lateSpikeT3 : playerTier === 4 ? 12 : 8;  // 18 shipped
      maxBid = Math.max(5, Math.min(tierSpike, Math.floor(budget * 0.4), hardCap));
      isMustFill = true;
    } else {
      // No slot pressure, not a late tier-spike: normally a $1-3 must-fill floor.
      // Tighten only for out-of-pool players — don't force a bid onto an
      // undraftable scrub when a draftable, undrafted player still exists at this
      // position. Leaving maxBid<=0 / isMustFill=false lets the end gate zero it so
      // the scrub falls to the user. Requires draftedPlayers to know what's left;
      // when it's null we keep the old behavior (never risk a roster hole).
      const skipOutOfPool = draftedPlayers && !isDraftable(player, settings) &&
        UNIQUE_PLAYERS.some(p => p.pos === pos && !draftedPlayers.has(p.name) && isDraftable(p, settings));
      if (!skipOutOfPool) {
        maxBid = Math.max(1, Math.min(hardCap, 3));
        isMustFill = true;
      }
    }
  }
  if (stillNeeded > 0 && maxBid > 0 && maxBid < 5 && pickNumber > 80 && playerTier <= 5 && budget > 15) {
    const lateSpike = playerTier <= 3 ? 18 : playerTier === 4 ? 12 : 8;
    const affordableLateSpike = Math.min(lateSpike, Math.floor(budget * 0.4), hardCap);
    if (affordableLateSpike > maxBid) maxBid = affordableLateSpike;
  }
  mkctx({ isMustFill, stillNeeded });
  mk("must-fill-net", maxBid);

  // === Universal surplus floor ===
  const surplusPerSlotFinal = effectiveBudget / Math.max(1, emptySlots);
  const totalPicks = (settings?.teams || 10) * 16;
  const pctDone = pickNumber / totalPicks;
  const pickUrgency = pctDone > 0.65 ? 1.5 : pctDone > 0.5 ? 1.25 : pctDone > 0.38 ? 1.1 : 1.0;
  const surplusThreshold = Math.max(1.5, Math.round(2 * (startingBudget / 200)));
  const isPuntPos = posBudget > 0 && posBudget < 15;
  // 1QB bench QB: don't let floors override QB2 cap (mirrors cap condition above)
  const isQb2Capped = pos === "QB" && sflexCount === 0 && posCount >= 1 &&
    ((profile.modifiers || []).includes("Punt QB") ||
     (profile.modifiers || []).includes("Elite QB") ||
     qbSpentForCap >= 25);
  if (surplusPerSlotFinal > surplusThreshold && !isPuntPos && !isQb2Capped) {
    let floor = Math.round(surplusPerSlotFinal * 0.7 * pickUrgency);
    // Junk tiers (bottom 3): cap the surplus-floor contribution at the player's
    // objective tier ceiling so early-draft war chests don't inflate scrubs to ~$9.
    // Mid tiers (1-6) untouched — surplus deployment still works there.
    const maxTier = TIER_MAPS[pos] ? Math.max(...TIER_MAPS[pos].map(t => t.tier)) : 9;
    if (playerTier >= maxTier - 2) floor = Math.min(floor, Math.round(tierCeil));
    maxBid = Math.max(maxBid, Math.min(floor, hardCap));
    mk("universal-surplus-floor", maxBid);
  }
  const greedyThreshold = Math.max(3, Math.round(5 * (startingBudget / 200)));
  if (playerTier <= 5 && surplusPerSlotFinal > greedyThreshold && emptySlots >= 3 && pctDone > 0.38 && !isQb2Capped) {
    // Skip greedy boost on Punt TE — war chest deploys on priority positions instead
    // Punt QB relies on the $18 absolute cap downstream, so greedy is allowed to fire
    const mods = profile.modifiers || [];
    if (!(pos === "TE" && mods.includes("Punt TE"))) {
      maxBid = Math.max(maxBid, Math.min(Math.floor(budget * 0.4), hardCap));
      mk("greedy-boost", maxBid);
    }
  }

  // TE cumulative spending cap — apply AFTER surplus/greedy so they can't override it
  // 15% of budget ($30 at $200) for all archetypes; 20% ($40) for Elite TE modifier
  if (pos === "TE" && posSpent > 0 && !isMustFill) {
    const isEliteTE = (profile.modifiers || []).includes("Elite TE");
    const teCap = Math.round(startingBudget * (isEliteTE ? 0.20 : 0.15));
    if (posSpent >= teCap) maxBid = 0;
  }

  // Patient Spender: tier ceiling cap — apply AFTER surplus/greedy so they can't override it
  // Never bid > 2x objective tier ceiling regardless of war chest size
  if (profile.modifiers?.includes("Patient Spender") && !isMustFill) {
    const rn = parseInt((posRank || '').replace(/[A-Z]+/g, ''), 10);
    const objTierObj = isNaN(rn) ? null : TIER_MAPS[pos]?.find(t => t.tier === (getTierForRank(pos, rn)?.tier));
    if (objTierObj) {
      const patientTierCap = Math.round(objTierObj.ceiling * startingBudget * posMult * 2);
      if (maxBid > patientTierCap) maxBid = patientTierCap;
      mk("ps-tier-ceiling-cap", maxBid);
    }
  }

  // === Overpay tendency ===
  if (profile.overpay && maxBid > 5) {
    maxBid += profile.overpay * (0.5 + Math.random() * 0.5);
    mk("overpay", maxBid);
  }

  // === SFLEX QB tier-based behavioral constraints ===
  if (sflexCount > 0 && pos === "QB") {
    const mods = profile.modifiers || [];
    // Punt SF: self-select out of elite QB bidding wars (tier 1-3)
    if (mods.includes("Punt QB SF") && playerTier <= 3) {
      maxBid *= _sflexDials.puntTierMult;   // 0.15 shipped — see _sflexDials
    }
    // Kings: aggressive on mid-tier QB2s (tier 3-7), won't settle for bottom-tier (tier 8+)
    if (mods.includes("Elite QB SF") && posCount >= 1) {
      if (playerTier >= 3 && playerTier <= 7) maxBid *= 2.5;
      else if (playerTier >= 8) maxBid *= 0.3;
    }
  }

  // === 1QB QB tier-based behavioral constraints ===
  if (sflexCount === 0 && pos === "QB") {
    const mods = profile.modifiers || [];
    // Punt QB: avoids premium QBs (tier 1-3) — punt teams target T4+ bargains only
    if (mods.includes("Punt QB") && playerTier <= 3) {
      maxBid *= 0.15;
    }
  }

  // === SOFT CAP — board-anchored excess damping (see _softCap at top) ===
  // Damps the excess over an allowance anchored on the player's DISPLAYED
  // price, so the ceiling is board-relative rather than a flat budget fraction.
  // Piecewise-linear with positive slopes throughout: monotone for all inputs.
  if (!isMustFill && !_softCap.disabled) {
    if (_softCap.legacy) {
      // Pre-Aug-6 form, retained for A/B only. Non-monotone by construction.
      const softCapThreshold = 0.30 * Math.min(1.0, 8 / skillStarters) * posMult;
      const bidPct = maxBid / startingBudget;
      if (bidPct > softCapThreshold) {
        const dampMult = 2.0 * ((settings?.teams || 10) / 10);
        const dampening = Math.max(0.50, 1.0 - ((bidPct - softCapThreshold) * dampMult));
        maxBid *= dampening;
        mk("soft-cap-damp", maxBid);
      }
    } else {
      // 🔴 THE ONE SUBSTITUTION. `sc` IS `_softCap` whenever no anchor rider is
      // attached — the same object, not a spread copy — so the default path is
      // byte-identical by construction rather than by matching field values.
      // The legacy arm above deliberately reads none of this: it has no
      // boardTerm/headroom to replace and exists only for A/B.
      const sc = anchorSC || _softCap;
      // Roster depth still moves the allowance: a Kings roster (10 skill
      // starters) has less budget per starter, so it tolerates less overpay.
      const depth = Math.min(1.0, 8 / skillStarters);
      // D3: the floor leg is flat at boardTerm 0 (the shipped form, taken
      // verbatim so the identity arm is bit-exact) and affine in the board
      // above it, pinned to today's value at a `boardPin` board.
      // The pin stays DERIVED and UNSCALED under an anchor — it is a board
      // price, not an anchor term, exactly as the k-sweep emulated it
      // (softcapProbe.mjs, "the pin stays DERIVED and unscaled").
      const boardPin = derivePin(dynamicPriceMap, startingBudget);
      const floorLeg = sc.boardTerm === 0
        ? sc.floorFrac * startingBudget * depth
        : (sc.floorFrac * startingBudget - boardPin * sc.boardTerm
           + sc.boardTerm * Math.max(1, dynPrice)) * depth;
      const anchor = Math.max(
        Math.max(1, dynPrice) * (1 + sc.headroom) * depth,
        floorLeg,
      );
      if (maxBid > anchor) {
        maxBid = anchor + (maxBid - anchor) * sc.excessPass;
        mk("soft-cap-damp", maxBid);
      }
    }
  }

  // Universal price enforcement — no elite player sells for a steal if teams have budget
  const compFloor = Math.round(tierValue * 0.40);
  if (tierValue >= 20 && maxBid > 0 && budget > compFloor && !isQb2Capped) {
    maxBid = Math.max(maxBid, compFloor);
    mkctx({ compFloor });
    mk("comp-floor", maxBid);
  }

  // Price enforcement: capped teams bid ONLY up to floor on elite players — enforce price, never win
  if (priceEnforceOnly) {
    if (tierValue >= 30 && budget > compFloor) {
      mk("exit:priceEnforceOnly-floor", Math.max(0, Math.min(compFloor, hardCap)));
      return Math.max(0, Math.min(compFloor, hardCap));
    }
    mk("exit:priceEnforceOnly-zero", 0);
    return 0;
  }

  // === ABSOLUTE CAPS ===
  // 🔴 This read is OUTSIDE the `!isMustFill && !_softCap.disabled` guard above,
  // so unlike the other four fields the anchor's absCapFrac also reaches
  // must-fill bids. Direction is safe: the anchor parks absCapFrac at 0.42 vs
  // the shipped 0.37, which RAISES the must-fill ceiling ($74 → $84 on $200)
  // rather than squeezing a forced buy. It is a safety rail, not a fitted term.
  const maxSinglePlayer = Math.round(startingBudget * (anchorSC || _softCap).absCapFrac);
  maxBid = Math.min(maxBid, maxSinglePlayer, hardCap);
  mk("absolute-caps", maxBid);

  // Punt QB: cap at 9% of budget in 1QB — scales across formats ($18/$200, $22/$240)
  // Always enforced, even on must-fill — punt teams never overpay for QB
  if (pos === "QB" && sflexCount === 0 && (profile.modifiers || []).includes("Punt QB")) {
    maxBid = Math.min(maxBid, Math.round(startingBudget * 0.09));
  }

  // Draftable-pool gate: the AI won't bid on out-of-pool (undraftable) players —
  // a user-nominated scrub goes to the user at $1. Must-fill overrides (isMustFill
  // stays sacred): if the team was forced to fill this slot and no draftable player
  // remained, the must-fill safety net already set the bid and we leave it alone.
  if (!isMustFill && !isDraftable(player, settings)) { maxBid = 0; mk("draftable-pool-gate", maxBid); }

  mk("final", Math.max(0, Math.round(maxBid)));
  return Math.max(0, Math.round(maxBid));
}

export function calcAiOpeningBid(profile, player, teamRoster, budget, emptySlots, isSelfBuy, teamObj, sflexCount = 0, settings = {}, dynamicPriceMap = null, aiRosterSize = MOCK_ROSTER_SIZE) {
  const pos = player.pos;
  const posRank = getAdpPosRank(player.name) || player.posRank;
  const dynPrice = dynamicPriceMap?.get(`${player.name}-${player.pos}`) || player.value;
  if (pos === "K" && (settings?.positions?.K ?? 1) === 0) return 0;
  if (pos === "DEF" || pos === "K") return isSelfBuy ? 2 : 1;
  if (!isSelfBuy) return 1; // poison pill: open low to bait bidders

  // Positional scarcity multiplier (same as calcAiMaxBidV2)
  const baselineSlots = { QB: 1, RB: 2.5, WR: 3.5, TE: 1 };
  const actualSlotsPerTeam = {
    QB: (settings?.positions?.QB || 1) + sflexCount,
    RB: (settings?.positions?.RB || 2) + (settings?.positions?.FLEX || 1) * 0.5,
    WR: (settings?.positions?.WR || 2) + (settings?.positions?.FLEX || 1) * 0.5,
    TE: (settings?.positions?.TE || 1),
  };
  let scarcityMultiplier = actualSlotsPerTeam[pos] / baselineSlots[pos];

  // SFLEX QB: rank-dependent scarcity curve, dampened by strategy (same as calcAiMaxBidV2)
  if (sflexCount > 0 && pos === "QB" && scarcityMultiplier !== 1) {
    const qbDemand = ((settings?.positions?.QB || 1) + sflexCount) * (settings?.teams || 10);
    const qbBaseline = 1 * 10;
    const demandRatio = qbDemand / qbBaseline;
    const qbRankRaw = parseInt((posRank || "").replace(/[A-Z]+/g, ""), 10) || 99;
    const mods = profile.modifiers || [];
    const dampener = mods.includes("Elite QB SF") ? 1.0
                   : mods.includes("Balanced QB SF") ? _sflexDials.balancedDampener  // 0.7 shipped
                   : mods.includes("Punt QB SF") ? _sflexDials.puntDampener          // 0.4 shipped
                   : 1.0;
    const scale = (qbRankRaw <= 3 ? 0.25
                : qbRankRaw <= 8 ? 0.35
                : qbRankRaw <= 14 ? 0.45
                : 0.55) * dampener;
    scarcityMultiplier = 1.0 + (demandRatio - 1.0) * scale;
  }

  // Look up player's tier midpoint from personal tier sheet
  const tierSheet = teamObj?.tierSheet;
  const playerTier = tierSheet?.get(player.name) || (() => {
    const rn = parseInt((posRank || '').replace(/[A-Z]+/g, ''), 10);
    return isNaN(rn) ? 99 : (getTierForRank(pos, rn)?.tier || 99);
  })();
  const tiers = TIER_MAPS[pos];
  const tierObj = tiers?.find(t => t.tier === playerTier) || null;
  // League-calibration rider — same principle as calcAiMaxBidV2: openings
  // anchor to the calibrated tier world. Absent → 1.0, byte-identical.
  const obLeagueMult = dynamicPriceMap?.__leagueMultipliers || null;
  const obPosMult = obLeagueMult ? (obLeagueMult[pos] ?? 1) : 1;
  // Seam A (superflex only, SHIPPED aug22). The nominator IS the opening
  // bidder — a tail QB nobody outbids sells at exactly this number — so leaving
  // the opening on the 1QB ladder would cap the seam's reach at the bids nobody
  // makes. No homer promotion here: the shipped opening bid has none either.
  const obQbBand = qbTailBandFor(pos, sflexCount, posRank, playerTier, false);
  const obBand = obQbBand || tierObj;
  const tierMid = obBand ? (obBand.floor + obBand.ceiling) / 2 * (settings?.budget || 200) * obPosMult : dynPrice;
  // Self-buy: open at 40-60% of tier midpoint, scaled by scarcity
  const openPct = 0.40 + Math.random() * 0.20;
  let opening = Math.round(tierMid * openPct * scarcityMultiplier);
  const hardCap = emptySlots <= 1 ? budget : budget - (emptySlots - 1);
  opening = Math.min(opening, hardCap);
  // Archetype caps on opening bid — same hard ceilings as calcAiMaxBidV2
  if (profile.starsMode) {
    const starCount = teamRoster.filter(p => p.price >= 40).length;
    if (starCount >= 3) {
      const ssSlots = Math.max(1, emptySlots);
      const ssSurplus = budget / ssSlots;
      if (ssSurplus > 3) {
        opening = Math.min(opening, Math.min(Math.round(ssSurplus * 0.5), 5));
      } else {
        opening = Math.min(opening, 3);
      }
    } else if (isSelfBuy && dynPrice >= 40) {
      // Star-hunting self-buy: open at minimum $40 to guarantee star price threshold
      opening = Math.max(opening, Math.min(40, hardCap));
    }
  }
  if (profile.rbCap < 999 && pos === "RB") opening = Math.min(opening, profile.rbCap);
  if (profile.heroRB && pos === "RB") {
    const hasHeroRB = teamRoster.some(p => p.player.pos === "RB" && (dynamicPriceMap?.get(`${p.player.name}-${p.player.pos}`) || p.player.value) >= 40);
    if (hasHeroRB) {
      opening = Math.min(opening, 5);
    } else if (isSelfBuy && dynPrice >= 40) {
      // Pre-hero self-buy: open at minimum $40 to secure elite RB
      opening = Math.max(opening, Math.min(40, hardCap));
    }
  }
  if (profile.rbSpendCap && pos === "RB") {
    const rbSpent = teamRoster.filter(p => p.player.pos === "RB").reduce((s, p) => s + p.price, 0);
    if (rbSpent >= profile.rbSpendCap) opening = 1;
    else opening = Math.min(opening, profile.rbSpendCap - rbSpent);
  }
  // Balanced: never open above $40
  if (profile.archetype === "Balanced") opening = Math.min(opening, 40);
  // Surplus-aware floor: teams with excess budget open higher to prevent $1 steals
  const surplusPerSlot2 = budget / Math.max(1, emptySlots);
  if (pos !== "DEF" && pos !== "K" && surplusPerSlot2 > 3) {
    const floor2 = Math.round(surplusPerSlot2 * 0.5);
    opening = Math.max(opening, Math.min(floor2, hardCap));
  }
  return Math.max(1, opening);
}

// Weighted selection from sorted candidates: #1 40%, #2 30%, #3 20%, #4 10%
export function weightedTopPick(sortedCandidates) {
  const pool = sortedCandidates.slice(0, Math.min(4, sortedCandidates.length));
  if (pool.length <= 1) return pool[0] || null;
  const weights = [0.40, 0.30, 0.20, 0.10].slice(0, pool.length);
  const total = weights.reduce((s, w) => s + w, 0);
  let r = Math.random() * total;
  for (let i = 0; i < weights.length; i++) { r -= weights[i]; if (r <= 0) return pool[i]; }
  return pool[pool.length - 1];
}

// getOpponentMaxBid — find the highest single bid any opponent can afford.
// Enables auto-buy nominations: if our opening bid exceeds this, no one can outbid us.
export function getOpponentMaxBid(team, allTeams, aiRosterSize = MOCK_ROSTER_SIZE) {
  let highest = 1;
  for (const t of Object.values(allTeams)) {
    if (t.name === team.name) continue;
    const emptySlots = aiRosterSize - t.roster.length;
    if (emptySlots <= 0) continue;
    const maxAffordable = emptySlots <= 1 ? t.budget : t.budget - (emptySlots - 1);
    if (maxAffordable > highest) highest = maxAffordable;
  }
  return highest;
}

export function pickAiNomination(profile, teamRoster, availablePlayers, pickNumber, teamObj, allTeams, sflexCount = 0, settings = {}, aiRosterSize = MOCK_ROSTER_SIZE) {
  const budgetScale = (settings?.budget || 200) / 200;
  const emptySlots = aiRosterSize - teamRoster.length;
  // Branch tag for _nomDiag (see the collector declaration at the top of this
  // file). Identity function when the collector is off.
  const NT = (tag, p) => {
    if (_nomDiag.data && p) {
      _nomDiag.data.push({
        pick: pickNumber, tag, player: p.name, pos: p.pos,
        nominator: teamObj?.name ?? null,
        archetype: teamObj?.profileKey ?? null,
        favoriteTeam: profile?.favoriteTeam ?? null,
        playerNflTeam: p.team ?? null,
      });
    }
    return p;
  };
  // Gate the AI's candidate pool to the settings-derived draftable set — the AI
  // never nominates undraftable deep players (the user still can, via the UI).
  // Guard against an empty pool (shouldn't happen given the 1.15 buffer).
  const draftablePool = availablePlayers.filter(p => isDraftable(p, settings));
  if (draftablePool.length > 0) availablePlayers = draftablePool;
  const posCounts = {};
  teamRoster.forEach(p => { posCounts[p.player.pos] = (posCounts[p.player.pos] || 0) + 1; });
  const kSlots = settings?.positions?.K ?? 1;
  // Hard cap: never nominate a 3rd QB in 1QB formats
  if (sflexCount === 0 && (posCounts["QB"] || 0) >= 2) {
    availablePlayers = availablePlayers.filter(p => p.pos !== "QB");
  }
  const posReqs = { QB: (settings?.positions?.QB || 1) + sflexCount, RB: 2, WR: 3, TE: 1, DEF: 1, K: kSlots };
  const urgentNeeds = [];
  for (const [pos, req] of Object.entries(posReqs)) {
    if ((posCounts[pos] || 0) < req) urgentNeeds.push(pos);
  }
  // Must-fill restriction: if unfilled starter slots >= empty slots, only nominate needed positions
  const minReqs = { QB: (settings?.positions?.QB || 1) + sflexCount, RB: 2, WR: 2, TE: 1, DEF: 1, K: kSlots };
  const unfilledStarters = [];
  for (const [pos, req] of Object.entries(minReqs)) {
    if ((posCounts[pos] || 0) < req) unfilledStarters.push(pos);
  }
  if (unfilledStarters.length > 0 && unfilledStarters.length >= emptySlots - 3) {
    availablePlayers = availablePlayers.filter(p => unfilledStarters.includes(p.pos));
  }
  // Tier-based value helper: uses personal tier sheet ceiling instead of static player.value
  const tierSheet = teamObj?.tierSheet;
  const getTierVal = (p) => {
    const tier = tierSheet?.get(p.name);
    if (tier) {
      const tObj = TIER_MAPS[p.pos]?.find(t => t.tier === tier);
      if (tObj) return tObj.ceiling * 200;
    }
    return p.value; // fallback to static value
  };
  // === Auto-buy scan: if no opponent can outbid us, nominate for a guaranteed steal ===
  if (allTeams && emptySlots >= 2) {
    const oppMax = getOpponentMaxBid(teamObj, allTeams, aiRosterSize);
    let bestAutoBuy = null;
    let bestAutoBuyValue = 0;
    for (const p of availablePlayers) {
      if (p.pos === "DEF" || p.pos === "K") continue;
      const myVal = getTierVal(p);
      if (oppMax >= 1 && oppMax <= myVal) {
        const autoBuyValue = myVal - oppMax;
        if (autoBuyValue > bestAutoBuyValue) {
          bestAutoBuyValue = autoBuyValue;
          bestAutoBuy = p;
        }
      }
    }
    if (bestAutoBuy) {
      bestAutoBuy.autoBuyPrice = oppMax;
      return NT('autobuy', bestAutoBuy);
    }
  }
  // Early K/DEF force-nomination: when slots are running low, guarantee these get filled
  if (emptySlots <= 7) {
    const needK = kSlots > 0 && (posCounts["K"] || 0) < kSlots;
    const needDEF = (posCounts["DEF"] || 0) < 1;
    if (needK || needDEF) {
      const forcePos = needK && needDEF
        ? (emptySlots <= 3 ? "K" : (Math.random() < 0.5 ? "K" : "DEF"))
        : needK ? "K" : "DEF";
      const forceCandidates = availablePlayers.filter(p => p.pos === forcePos).sort((a, b) => getTierVal(b) - getTierVal(a));
      if (forceCandidates.length > 0) return NT('force-defk', weightedTopPick(forceCandidates));
    }
  }
  // SFLEX QB2 nomination: teams with 1 QB actively pursue QB2
  // Strategy-aware: Kings aggressive (55%), Moderate normal (40%), Punt patient (25%)
  const mods = profile.modifiers || [];
  let qb2NomRate = 0;
  if (mods.includes("Elite QB SF")) qb2NomRate = 0.55;
  else if (mods.includes("Balanced QB SF")) qb2NomRate = 0.40;
  else if (mods.includes("Punt QB SF")) qb2NomRate = 0.25;
  // Elite QB SF: force QB2 nomination past pick 40
  if (mods.includes("Elite QB SF") && (posCounts["QB"] || 0) === 1 && pickNumber > 40) {
    qb2NomRate = 0.90;
  }
  if (sflexCount > 0 && qb2NomRate > 0 && (posCounts["QB"] || 0) === 1 && emptySlots >= 4 && Math.random() < qb2NomRate) {
    const qbCandidates = availablePlayers.filter(p => p.pos === "QB").sort((a, b) => getTierVal(b) - getTierVal(a));
    if (qbCandidates.length > 0) {
      // Prefer mid-tier QBs (index 2-8) over elite to create diverse price outcomes
      const midStart = Math.min(2, qbCandidates.length - 1);
      const midEnd = Math.min(8, qbCandidates.length);
      const midTierQBs = qbCandidates.slice(midStart, midEnd);
      if (midTierQBs.length > 0) return NT('sflex-qb2', weightedTopPick(midTierQBs));
      return NT('sflex-qb2', weightedTopPick(qbCandidates));
    }
  }
  // Must-fill: urgent positions when slots are running out
  if (urgentNeeds.length > 0 && emptySlots <= urgentNeeds.length + 6) {
    for (const pos of urgentNeeds) {
      const candidates = availablePlayers.filter(p => p.pos === pos).sort((a, b) => getTierVal(b) - getTierVal(a));
      if (candidates.length > 0) return NT('mustfill', weightedTopPick(candidates));
    }
  }

  // === Endgame nomination intelligence (picks 81+) ===
  if (pickNumber > 80 && allTeams) {
    const myName = teamObj?.name;
    const myBudget = teamObj?.budget || 0;
    const myMaxBid = emptySlots <= 1 ? myBudget : myBudget - (emptySlots - 1);

    // Scan the room: compute each rival's max bid capacity and positional needs
    let highestRivalMaxBid = 0;
    const rivalNeedsByPos = {}; // pos → [{name, maxBid}]
    Object.values(allTeams).forEach(t => {
      if (t.name === myName) return;
      const tSlots = aiRosterSize - t.roster.length;
      if (tSlots <= 0) return;
      const tMaxBid = tSlots <= 1 ? t.budget : t.budget - (tSlots - 1);
      if (tMaxBid > highestRivalMaxBid) highestRivalMaxBid = tMaxBid;
      const tPosCnts = {};
      t.roster.forEach(r => { tPosCnts[r.player.pos] = (tPosCnts[r.player.pos] || 0) + 1; });
      const tPosReqs = { QB: (settings?.positions?.QB || 1) + sflexCount, RB: 2, WR: 2, TE: 1, DEF: 1, K: kSlots };
      for (const [p, req] of Object.entries(tPosReqs)) {
        if ((tPosCnts[p] || 0) < req) {
          if (!rivalNeedsByPos[p]) rivalNeedsByPos[p] = [];
          rivalNeedsByPos[p].push({ name: t.name, maxBid: tMaxBid });
        }
      }
    });

    // Rule 1: Don't nominate own myGuys when rivals can outbid us — drain their budgets first
    const myGuyNames = (teamObj?.myGuys || []).map(g => g.name);
    const availableMyGuys = myGuyNames
      .map(name => availablePlayers.find(p => p.name === name))
      .filter(Boolean);
    const safeMyGuys = availableMyGuys.filter(() => highestRivalMaxBid < myMaxBid);

    // If we have safe myGuys (no rival can outbid us), nominate one
    if (safeMyGuys.length > 0) {
      return NT('eg-myguy', safeMyGuys[Math.floor(Math.random() * safeMyGuys.length)]);
    }

    // Rule 2: Nominate players that match rival positional needs to force them to spend
    // Find positions where rivals still have needs and budget
    const drainCandidates = [];
    for (const [rPos, rivals] of Object.entries(rivalNeedsByPos)) {
      // Only drain positions WE don't urgently need
      if (urgentNeeds.includes(rPos)) continue;
      const richRivals = rivals.filter(r => r.maxBid >= 5);
      if (richRivals.length >= 1) {
        const candidates = availablePlayers
          .filter(p => p.pos === rPos && getTierVal(p) >= 5)
          .sort((a, b) => getTierVal(b) - getTierVal(a));
        if (candidates.length > 0) drainCandidates.push(candidates[0]);
      }
    }
    if (drainCandidates.length > 0 && Math.random() < 0.60) {
      // Nominate the drain candidate at the position with richest/most rivals
      drainCandidates.sort((a, b) => {
        const aRivals = rivalNeedsByPos[a.pos]?.length || 0;
        const bRivals = rivalNeedsByPos[b.pos]?.length || 0;
        return bRivals - aRivals;
      });
      return NT('eg-drain', drainCandidates[0]);
    }

    // Rule 3: If myGuys exist but aren't safe, DON'T nominate them — fall through to standard logic
    if (availableMyGuys.length > 0 && highestRivalMaxBid >= myMaxBid) {
      // Skip myGuy nomination — fall through to standard weighted logic
      // which will nominate a non-myGuy to burn rival budgets
    }
  }

  // === Endgame sleeper nomination (picks >= 120 or emptySlots <= 6, 80% chance) ===
  // At $1 per pick, nominating your sleeper guarantees you get them
  if (pickNumber >= 120 || emptySlots <= 6) {
    const sleeperNames = (teamObj?.sleepers || []).map(g => g.name);
    const rosterNames = new Set(teamRoster.map(p => p.player.name));
    const availableSleepers = sleeperNames
      .filter(name => !rosterNames.has(name))
      .map(name => availablePlayers.find(p => p.name === name))
      .filter(Boolean);
    if (availableSleepers.length > 0 && Math.random() < 0.80) {
      return NT('eg-sleeper', availableSleepers[Math.floor(Math.random() * availableSleepers.length)]);
    }
  }

  // === Late My Guy safe nomination (picks >= 100, 70% chance) ===
  // Nominate a My Guy only when no rival can outbid us
  if (pickNumber >= 100 && allTeams) {
    const myGuyNames = (teamObj?.myGuys || []).map(g => g.name);
    const rosterNames = new Set(teamRoster.map(p => p.player.name));
    const myBudgetLate = teamObj?.budget || 0;
    const myMaxBidLate = emptySlots <= 1 ? myBudgetLate : myBudgetLate - (emptySlots - 1);
    const safeMyGuys = myGuyNames
      .filter(name => !rosterNames.has(name))
      .map(name => availablePlayers.find(p => p.name === name))
      .filter(Boolean)
      .filter(p => {
        // Check if any rival can outbid us on this player
        const rivalMax = Object.values(allTeams).reduce((max, t) => {
          if (t.name === teamObj?.name) return max;
          const tEmpty = aiRosterSize - (t.roster?.length || 0);
          const tMax = tEmpty <= 1 ? (t.budget || 0) : (t.budget || 0) - (tEmpty - 1);
          return Math.max(max, tMax);
        }, 0);
        return myMaxBidLate > rivalMax; // safe — we can outbid everyone
      });
    if (safeMyGuys.length > 0 && Math.random() < 0.70) {
      return NT('late-myguy', safeMyGuys[Math.floor(Math.random() * safeMyGuys.length)]);
    }
  }

  // === Homer self-nomination preference (60% chance) ===
  // When a Homer team nominates, 60% of the time prefer a player from their favorite team.
  // Strongest preference — placed before tax-the-room in waterfall.
  if (profile?.favoriteTeam && Math.random() < 0.60) {
    const homerCandidates = availablePlayers
      .filter(p => p.team === profile.favoriteTeam && p.pos !== "DEF" && p.pos !== "K")
      .sort((a, b) => getTierVal(b) - getTierVal(a));
    if (homerCandidates.length > 0) return NT('homer', homerCandidates[0]);
  }

  // === Tax-the-room: nominate high-value players at positions this team has filled ===
  // Forces rivals to spend on mid-tier starters the nominator doesn't need.
  // Graduated probability keeps mid-tier supply flowing through Rd3-6.
  const taxRate = pickNumber <= 10 ? 0.50
               : pickNumber <= 20 ? 0.70
               : pickNumber <= 30 ? 0.60
               : pickNumber <= 40 ? 0.40
               : pickNumber <= 50 ? 0.75
               : pickNumber <= 65 ? 0.65
               : pickNumber <= 70 ? 0.40
               : 0;
  // Three distinct rates the probe needs to separate: how often the waterfall
  // REACHES this branch at all (earlier branches return first), how often the
  // roll passes, and how often a candidate actually exists.
  const taxRoll = taxRate > 0 && Math.random() < taxRate;
  if (_nomDiag.data) _nomDiag.data.push({ pick: pickNumber, tag: '_tax-gate', taxRate, taxRoll });
  if (taxRoll) {
    let bestTaxPos = null;
    let bestTaxPlayer = null;
    let bestTaxAppeal = 0;
    for (const pos of ["QB", "RB", "WR", "TE"]) {
      const req = posReqs[pos] || 1;
      const filledRatio = Math.min((posCounts[pos] || 0) / req, 1.0);
      const candidates = availablePlayers.filter(p => p.pos === pos).sort((a, b) => getTierVal(b) - getTierVal(a));
      if (candidates.length === 0) continue;
      const best = candidates[0];
      const tierVal = getTierVal(best);
      if (tierVal < 8) continue;
      const taxAppeal = filledRatio * tierVal;
      if (taxAppeal > bestTaxAppeal) {
        bestTaxAppeal = taxAppeal;
        bestTaxPos = pos;
        bestTaxPlayer = best;
      }
    }
    if (bestTaxPlayer) return NT('tax-room', bestTaxPlayer);
  }

  // === Tax-the-homer: non-homer teams nominate players from homer teams' NFL rosters ===
  // ~20% chance — forces homer teams to spend their reserve or lose their guys.
  // Only skill players with tierValue >= $8 to avoid lone-bidder problem on scrubs.
  if (!profile?.favoriteTeam && Math.random() < 0.20) {
    const homerTeamNFLs = [];
    if (allTeams) {
      for (const t of Object.values(allTeams)) {
        if (t.profile?.favoriteTeam && (t.profile?.modifiers || []).includes('Homer')) {
          homerTeamNFLs.push(t.profile.favoriteTeam);
        }
      }
    }
    if (homerTeamNFLs.length > 0) {
      const targetNFL = homerTeamNFLs[Math.floor(Math.random() * homerTeamNFLs.length)];
      const taxHomerCandidates = availablePlayers
        .filter(p => p.team === targetNFL && p.pos !== "DEF" && p.pos !== "K" && getTierVal(p) >= 8)
        .sort((a, b) => getTierVal(b) - getTierVal(a));
      if (taxHomerCandidates.length > 0) return NT('tax-homer', taxHomerCandidates[0]);
    }
  }

  // === Nomination tactic: self-nominate sleepers (graduated rate) ===
  // 15% at picks 40-65 (occasional, keeps mid-tier starters flowing)
  // 50% at picks 66-90 (original rate, sleeper window)
  if (pickNumber >= 40 && pickNumber <= 90) {
    const sleeperRate = pickNumber <= 65 ? 0.15 : 0.50;
    const sleeperNames = (teamObj?.sleepers || []).map(g => g.name);
    const rosterNames = new Set(teamRoster.map(p => p.player.name));
    const availableSleepers = sleeperNames
      .filter(name => !rosterNames.has(name))
      .map(name => availablePlayers.find(p => p.name === name))
      .filter(Boolean);
    if (availableSleepers.length > 0 && Math.random() < sleeperRate) {
      return NT('sleeper', availableSleepers[Math.floor(Math.random() * availableSleepers.length)]);
    }
  }

  // === Nomination tactic: self-nominate myGuys (60% chance) ===
  const myGuyNoms = (teamObj?.myGuys || []).map(g => g.name);
  if (myGuyNoms.length > 0 && Math.random() < 0.60) {
    const availableMyGuys = myGuyNoms
      .map(name => availablePlayers.find(p => p.name === name))
      .filter(Boolean);
    if (availableMyGuys.length > 0) {
      return NT('myguy', availableMyGuys[Math.floor(Math.random() * availableMyGuys.length)]);
    }
  }

  // === Nomination tactic: nominate filled positions to inflate price for rivals ===
  // After filling a starter slot, nominate the next-best at that position
  if (Math.random() < 0.35) {
    const starterReqs = { QB: 1, RB: 2, WR: 2, TE: 1 };
    Object.entries(profile.starterOverrides || {}).forEach(([p, c]) => { starterReqs[p] = c; });
    const filledPositions = [];
    for (const [pos, req] of Object.entries(starterReqs)) {
      if ((posCounts[pos] || 0) >= req) filledPositions.push(pos);
    }
    if (filledPositions.length > 0) {
      // Pick a random filled position and nominate the best available player there
      const inflatePos = filledPositions[Math.floor(Math.random() * filledPositions.length)];
      const inflateCandidates = availablePlayers
        .filter(p => p.pos === inflatePos && getTierVal(p) >= 15)
        .sort((a, b) => getTierVal(b) - getTierVal(a));
      if (inflateCandidates.length > 0) return NT('inflate', weightedTopPick(inflateCandidates));
    }
  }

  // === Stars & Scrubs: nominate RB/WR stars aggressively during star-hunting ===
  if (profile.starsMode) {
    const starPicks = teamRoster.filter(p => p.price >= 40);
    const starCount = starPicks.length;
    if (starCount < 3) {
      const starPosCounts = {};
      starPicks.forEach(p => { starPosCounts[p.player.pos] = (starPosCounts[p.player.pos] || 0) + 1; });
      // Priority: RB/WR stars only — that's where S&S budget goes
      const rbwrStars = availablePlayers.filter(p =>
        (p.pos === "RB" || p.pos === "WR") && getTierVal(p) >= 40 && (starPosCounts[p.pos] || 0) < 2
      ).sort((a, b) => getTierVal(b) - getTierVal(a));
      if (rbwrStars.length > 0) return NT('stars', weightedTopPick(rbwrStars));
      // Fallback: any RB/WR with decent value
      const rbwrFallback = availablePlayers.filter(p =>
        (p.pos === "RB" || p.pos === "WR") && getTierVal(p) >= 20 && (starPosCounts[p.pos] || 0) < 2
      ).sort((a, b) => getTierVal(b) - getTierVal(a));
      if (rbwrFallback.length > 0) return NT('stars', weightedTopPick(rbwrFallback));
      // Last resort: any position star (QB/TE can work if RB/WR exhausted)
      const anyStars = availablePlayers.filter(p =>
        getTierVal(p) >= 40 && (starPosCounts[p.pos] || 0) < 2
      ).sort((a, b) => getTierVal(b) - getTierVal(a));
      if (anyStars.length > 0) return NT('stars', weightedTopPick(anyStars));
    }
    // After 3 stars: fall through to standard logic for cheap nominations
  }

  // === Hero RB: nominate elite RBs until hero is secured ===
  if (profile.heroRB && !teamObj?.heroRBDrafted && pickNumber <= 55) {
    // After pick 35: widen to T1-T3 RBs (tier ceiling >= $30)
    const heroThreshold = pickNumber > 35 ? 30 : 40;
    const topRBs = availablePlayers.filter(p => p.pos === "RB" && getTierVal(p) >= heroThreshold).sort((a, b) => getTierVal(b) - getTierVal(a));
    if (topRBs.length > 0) return NT('herorb', weightedTopPick(topRBs));
    // No candidates left: fall through to standard logic
  }

  // === WR Heavy FLEX: actively nominate WRs for FLEX slot when WR1-3 are filled ===
  if (profile.flexPos === "WR" && (profile.starterOverrides?.WR || 2) === 4) {
    const wrCount = teamRoster.filter(p => p.player.pos === "WR").length;
    if (wrCount === 3) {
      // Need WR4 for FLEX — target mid-tier WRs (tier ceiling $10-35)
      const flexWRs = availablePlayers.filter(p => p.pos === "WR" && getTierVal(p) >= 10 && getTierVal(p) <= 35).sort((a, b) => getTierVal(b) - getTierVal(a));
      if (flexWRs.length > 0 && Math.random() < 0.85) return NT('wrflex', weightedTopPick(flexWRs));
    }
  }

  // The tiers this seat is still shopping for — the unfilled shopping-list
  // slots, keyed "pos:tier". Lifted verbatim out of the last-in-tier block
  // below (which is still its only other caller) so the hold-aware branch
  // cannot drift from it: both must mean the same thing by "my targets".
  const buildNeededTiers = () => {
    const neededTiers = new Map(); // Map<"pos:tier", {pos, tier}>
    for (const [slotKey, slotDef] of Object.entries(profile.shoppingList)) {
      const slotPos = slotDef.pos === "ANY" ? null : (slotDef.pos || slotKey.replace(/[0-9]/g, ''));
      if (!slotPos || slotPos === "STAR") continue;
      const slotNum = parseInt(slotKey.replace(/[A-Z]+/g, ''), 10) || 1;
      const filled = posCounts[slotPos] || 0;
      if (slotKey === "FLEX") {
        const starterReq = (profile.starterOverrides?.[slotPos] || (slotPos === "RB" || slotPos === "WR" ? 2 : 1));
        if (filled > starterReq) continue;
      } else if (filled >= slotNum) continue;
      for (const tier of slotDef.tiers) {
        neededTiers.set(`${slotPos}:${tier}`, { pos: slotPos, tier });
      }
    }
    return neededTiers;
  };

  // === The Hammer: when HOLDING the hammer, put its OWN target on the block ===
  // Probe seam for the aug19 hold-aware nomination measurement. Default OFF
  // (`_hammerArm.nominateWhenHolding`) and sflex-scoped, so 1QB rooms and every
  // shipped path are byte-identical — no Math.random() is drawn while the gate
  // is closed, which is what the probe's digest controls prove.
  //
  // The hypothesis: the seat can only USE the hammer when a player it wants is
  // on the block, and at HEAD it never arranges that for itself. While holding,
  // nominating its own target spends the leverage at the moment it exists.
  // Precedence is deliberate — this runs BEFORE the poison pill, so a holding
  // seat spends instead of draining. When NOT holding, the pill and everything
  // downstream run exactly as shipped.
  if (_hammerArm.nominateWhenHolding && sflexCount > 0
      && profile.archetype === "The Hammer"
      && tierSheet && profile.shoppingList && allTeams && teamObj) {
    // "Holds the hammer" is the SAME rank comparison the bidding block runs
    // (:1016-1028): my `budget - (emptySlots - 1)` capacity against every
    // rival's. getOpponentMaxBid (:1521) is the same expression but floors at
    // 1, so it is deliberately NOT reused here — the two definitions of
    // "holding" must not be able to drift by an off-by-one.
    const myCap = emptySlots <= 1 ? teamObj.budget : teamObj.budget - (emptySlots - 1);
    let topRivalCap = 0;
    for (const t of Object.values(allTeams)) {
      if (t.name === teamObj.name) continue;
      const tSlots = aiRosterSize - t.roster.length;
      if (tSlots <= 0) continue;
      const c = tSlots <= 1 ? t.budget : t.budget - (tSlots - 1);
      if (c > topRivalCap) topRivalCap = c;
    }
    if (myCap > topRivalCap) {
      const holdNoms = [];
      for (const [, { pos, tier }] of buildNeededTiers()) {
        for (const p of availablePlayers) {
          if (p.pos === pos && tierSheet.get(p.name) === tier) holdNoms.push(p);
        }
      }
      if (holdNoms.length > 0) {
        holdNoms.sort((a, b) => getTierVal(b) - getTierVal(a));
        return NT('hammer-hold', holdNoms[0]);
      }
      // Holding, but no unfilled shopping-list tier has a player available.
      // Fall through to shipped behaviour — never force a nomination.
    }
  }

  // === The Hammer: early = poison pill elite players to drain other budgets ===
  // Probe-only seam for the aug17 deletion-gap measurement: the pill fires for any
  // profile flagged `poisonPill: true`, decoupling the mechanism from the archetype
  // name. No shipped profile carries the flag, so this is byte-inert at HEAD.
  // (`profile.poisonRate` remains dead — the 0.7 literal below is deliberate.)
  if ((profile.archetype === "The Hammer" || profile.poisonPill === true) && pickNumber <= 50) {
    const elitePlayers = availablePlayers.filter(p => getTierVal(p) >= 40).sort((a, b) => getTierVal(b) - getTierVal(a));
    if (elitePlayers.length > 0 && Math.random() < 0.7) return NT('hammer', weightedTopPick(elitePlayers));
  }

  // DEF/K "safe pass" nomination — picks 71-100
  // Real Smithtown data: 4-6 DEF/K nominated in this range
  // Managers use these when they don't want to expose targets
  if (pickNumber >= 71 && pickNumber <= 100) {
    const needK = (kSlots > 0) && (posCounts["K"] || 0) < kSlots;
    const needDEF = (posCounts["DEF"] || 0) < 1;
    if (needK || needDEF) {
      // 35% chance to nominate DEF/K as safe pass
      if (Math.random() < 0.35) {
        const safePos = needK && needDEF
          ? (Math.random() < 0.5 ? "K" : "DEF")
          : needK ? "K" : "DEF";
        const safeCandidates = availablePlayers.filter(p => p.pos === safePos);
        if (safeCandidates.length > 0) {
          safeCandidates.sort((a, b) => getTierVal(b) - getTierVal(a));
          return NT('safepass-defk', safeCandidates[0]);
        }
      }
    }
  }

  // === Nomination tactic: last-in-tier — force bidding war on scarce tier players ===
  // If a tier the AI needs has only 1-2 players left, nominate one (poison pill gold)
  if (tierSheet && profile.shoppingList && Math.random() < 0.50) {
    const neededTiers = buildNeededTiers();
    // Check each needed tier for scarcity on this AI's personal sheet
    const scarceNoms = [];
    for (const [, { pos, tier }] of neededTiers) {
      const remaining = availablePlayers.filter(p => p.pos === pos && tierSheet.get(p.name) === tier);
      if (remaining.length >= 1 && remaining.length <= 2) {
        scarceNoms.push(...remaining);
      }
    }
    if (scarceNoms.length > 0) {
      // Nominate the highest-value scarce player
      scarceNoms.sort((a, b) => getTierVal(b) - getTierVal(a));
      return NT('scarce-tier', scarceNoms[0]);
    }
  }

  // === Standard nomination logic ===
  const isMid = pickNumber > 50 && pickNumber <= 80;
  const selfBuy = pickNumber > 80 ? true : Math.random() < (profile.selfNomRate + (isMid ? 0.2 : 0));

  if (pickNumber >= 11 && pickNumber <= 20) {
    const premiumPositions = sflexCount > 0
      ? availablePlayers.filter(p => (p.pos === "RB" || p.pos === "WR" || p.pos === "QB") && getTierVal(p) >= 30)
      : availablePlayers.filter(p => (p.pos === "RB" || p.pos === "WR") && getTierVal(p) >= 30);
    premiumPositions.sort((a, b) => getTierVal(b) - getTierVal(a));
    if (premiumPositions.length > 0 && Math.random() < 0.7) return NT('premium-11-20', weightedTopPick(premiumPositions));
  }

  const teamCount = allTeams ? Object.keys(allTeams).length : 10;
  const getBaseWeights = () => {
    if (sflexCount > 0) {
      if (pickNumber <= teamCount) return { QB: 0.40, RB: 0.28, WR: 0.25, TE: 0.07 };
      if (pickNumber <= 30) return { QB: 0.28, RB: 0.305, WR: 0.325, TE: 0.09 };           // DEF: 0 (was 0.05), split to RB+WR
      if (pickNumber <= 40) return { QB: 0.22, RB: 0.33, WR: 0.345, TE: 0.11 };            // DEF: 0, K: 0 (was 0.10+0.05), split to RB+WR
      if (pickNumber <= 60) return { QB: 0.22, RB: 0.30, WR: 0.32, TE: 0.11, DEF: 0.05 }; // K: 0 (was 0.05), DEF: 0.05 (was 0.10), split to RB+WR
      if (pickNumber <= 100) return { QB: 0.18, RB: 0.22, WR: 0.25, TE: 0.10, DEF: 0.12, K: 0.13 };
      return { QB: 0.15, RB: 0.20, WR: 0.20, TE: 0.09, DEF: 0.18, K: 0.18 };
    }
    if (pickNumber <= teamCount) return { QB: 0.28, RB: 0.35, WR: 0.30, TE: 0.07 };
    if (pickNumber <= 30) return { QB: 0.18, RB: 0.345, WR: 0.375, TE: 0.10 };
    if (pickNumber <= 60) return { QB: 0.15, RB: 0.315, WR: 0.335, TE: 0.12, DEF: 0.04 };
    if (pickNumber <= 100) return { QB: 0.12, RB: 0.25, WR: 0.28, TE: 0.10, DEF: 0.12, K: 0.13 };
    return { QB: 0.10, RB: 0.22, WR: 0.22, TE: 0.10, DEF: 0.18, K: 0.18 };
  };
  const baseWeights = getBaseWeights();
  const pickWeightedPos = (weights) => {
    const entries = Object.entries(weights).filter(([, w]) => w > 0);
    const total = entries.reduce((s, [, w]) => s + w, 0);
    if (total === 0) return null;
    let r = Math.random() * total;
    for (const [pos, w] of entries) { r -= w; if (r <= 0) return pos; }
    return entries[entries.length - 1][0];
  };
  const posSpending = {};
  teamRoster.forEach(p => { posSpending[p.player.pos] = (posSpending[p.player.pos] || 0) + p.price; });
  const filterCandidates = (candidates, pos) => {
    if (pos === "TE" && pickNumber < 25) return candidates.filter(p => getTierVal(p) >= 15);
    return candidates;
  };
  if (selfBuy) {
    const adjusted = {};
    for (const [pos, baseW] of Object.entries(baseWeights)) {
      const posKey = (pos === "DEF" || pos === "K") ? null : pos.toLowerCase() + "$";
      const posBudget = posKey ? Math.round((profile[posKey] || 0) * budgetScale) : 2;
      const remaining = Math.max(0, posBudget - (posSpending[pos] || 0));
      adjusted[pos] = baseW * (1 + remaining / 30);
    }
    for (let attempt = 0; attempt < 4; attempt++) {
      const pos = pickWeightedPos(adjusted);
      if (!pos) break;
      const candidates = filterCandidates(availablePlayers.filter(p => p.pos === pos), pos).sort((a, b) => getTierVal(b) - getTierVal(a));
      if (candidates.length > 0) return NT('std-selfbuy', weightedTopPick(candidates));
      delete adjusted[pos];
    }
  } else {
    const adjusted = {};
    for (const [pos, baseW] of Object.entries(baseWeights)) {
      if (pos === "DEF" || pos === "K") continue;
      const posBudget = Math.round((profile[pos.toLowerCase() + "$"] || 0) * budgetScale);
      adjusted[pos] = baseW * (50 / Math.max(5, posBudget));
    }
    for (let attempt = 0; attempt < 3; attempt++) {
      const pos = pickWeightedPos(adjusted);
      if (!pos) break;
      const candidates = filterCandidates(availablePlayers.filter(p => p.pos === pos), pos).sort((a, b) => getTierVal(b) - getTierVal(a));
      if (candidates.length > 0) return NT('std-weighted', weightedTopPick(candidates));
      delete adjusted[pos];
    }
  }
  return NT('std-fallback', weightedTopPick(availablePlayers.sort((a, b) => getTierVal(b) - getTierVal(a))) || availablePlayers[0]);
}
